import {useState, useMemo, useEffect, useRef, useCallback} from "react";
import { createPortal } from "react-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid, Customized } from "recharts";
import { useTheme } from "../lib/ThemeContext.jsx";
import {row, rowBetween, card, subText} from "../lib/styles.js";
import { LS } from "../lib/storage.js";
import {SaveBadge, Btn, ConfirmSheet, BottomSheet, Info, Tooltip, StyledSelect, Dropdown} from "../components/ui.jsx";
import MktSelector from "../components/MktSelector.jsx";
import { FONT, RADIUS, SPRING, ORDER_COLORS } from "../lib/theme.js";
import {mkKey, MONTH_NAMES, parseDate, computeCurrentStock, computeStockoutDate, computeInventorySummary, effectivePayoutForMkt, computeBreakevenUnits, generateOrderExpenses, generateRecurringItems, computeDeliveryExpenses, getOrderCashEvents, getPoolForMarketplace, getEffectivePool, synthesizePoolForSelection, fmtFull, fmt, currSym, getDaysInMonth} from "../lib/calculations.js";
import { formatDate, getDateFormat } from "../lib/format.js";
import { aggregateLeadTimes, getEffectiveLeadTime } from "./orders/orderUtils.js";
import { escHtml } from "../lib/safeguards.js";
import { useCached } from "../lib/computeCache.js";
import { allocateProductTargets, productPctKey, clearProductOverrides } from "../lib/calc/productTargets.js";

const MKT_COLORS = [
  "#FF3B30","#FF9500","#FFCC00","#34C759","#00C7BE",
  "#8b5cf6","#5E5CE6","#BF5AF2","#FF2D55","#30D158",
  "#64D2FF","#FFD60A","#FF6961","#4CD964","#007AFF",
];

// ── Target-history modal ───────────────────────────────────────────────────
// Small modal that lists every recorded change to a month's target along
// with a line-chart preview. Reads the `targetHistory` array that
// useData.updateTarget started writing (Phase 8 redesign).
function TargetHistoryModal({ T, monthLabel, currentTarget, history, onClose }) {
  const sorted = (history || []).slice().reverse(); // oldest → newest for the chart
  const series = sorted.map((entry, i) => ({
    idx: i,
    label: new Date(entry.ts).toLocaleDateString(undefined, { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }),
    value: Number(entry.value) || 0,
  }));
  // Always append the current value as the trailing point so the chart
  // reaches today even if the recorded history is empty.
  series.push({
    idx: series.length, label: "Now",
    value: Number(currentTarget) || 0,
  });
  return createPortal(
    <>
      <div onClick={onClose} style={{
        position:"fixed", inset: 0, zIndex: 10010,
        background:"rgba(0,0,0,0.32)", backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)",
      }}/>
      <div role="dialog" aria-modal="true" aria-label={`${monthLabel} target history`} style={{
        position:"fixed", left:"50%", top:"50%", transform:"translate(-50%,-50%)",
        zIndex: 10011, width:"min(520px, calc(100vw - 40px))", maxHeight:"90vh",
        background: T.card, border:`1px solid ${T.border}`, borderRadius: 20,
        boxShadow:"0 40px 80px rgba(0,0,0,0.5)", overflow:"hidden",
        fontFamily: FONT, display:"flex", flexDirection:"column",
      }}>
        <div style={{
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"14px 18px", borderBottom:`1px solid ${T.border}`,
        }}>
          <div>
            <div style={{ fontSize: 11, color: T.textSub, fontWeight: 700, textTransform:"uppercase", letterSpacing: 0.4 }}>Target history</div>
            <div style={{ fontSize: 17, color: T.text, fontWeight: 800, marginTop: 2 }}>{monthLabel}</div>
          </div>
          <button onClick={onClose} style={{
            background:"transparent", border:"none", color: T.textSub,
            cursor:"pointer", fontSize: 18, padding:"4px 8px",
          }}>✕</button>
        </div>
        <div style={{ padding:"14px 18px", flex: 1, overflow:"auto" }}>
          {series.length <= 1 ? (
            <div style={{
              padding:"24px 16px", textAlign:"center", fontSize: 13, color: T.textSub,
              border:`1px dashed ${T.border}`, borderRadius: 12,
            }}>No edits recorded yet. Each change you make to this month's target will appear here.</div>
          ) : (
            <>
              <div style={{ height: 180, marginBottom: 18 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="2 4" stroke={T.border}/>
                    <XAxis dataKey="idx" hide/>
                    <YAxis tick={{ fontSize: 10, fill: T.textSub }} domain={["auto","auto"]}/>
                    <RTooltip
                      contentStyle={{ background: T.card, border:`1px solid ${T.border}`, borderRadius: 8, fontSize: 11, fontFamily: FONT }}
                      formatter={(v) => v.toLocaleString()}
                      labelFormatter={(idx) => series[idx]?.label || ""}/>
                    <Line type="stepAfter" dataKey="value" stroke={T.accent} strokeWidth={2} dot={{ r: 3 }}/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap: 4 }}>
                {sorted.slice().reverse().map((entry, i) => (
                  <div key={i} style={{
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    padding:"8px 12px", background: T.surface, borderRadius: 8,
                    border:`1px solid ${T.border}`, fontSize: 12,
                  }}>
                    <span style={{ color: T.textSub }}>{new Date(entry.ts).toLocaleString(undefined, { day:"numeric", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" })}</span>
                    <span style={{ color: T.text, fontWeight: 700, fontVariantNumeric:"tabular-nums" }}>{Number(entry.value).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

function MonthCard({ year, monthIdx, months, marketplaces, orders, monthOps, saveStatus, selMkts, showDanger, dangerPct, isPrev, currentYear, lastEditedKey, setLastEditedKey, animKey, animDelay, can, onEditNote, modifiers, initialStock: initStock, weightedPayout, costPerUnit, shrinkagePct, simPct, monthBalance=0, simMonthBalance=0, isPoolSelection=false, poolPct=1, selectedPoolName="" }) {
  const T = useTheme();
  const isMobileCard = typeof window !== "undefined" && window.innerWidth < 768;
  const key = mkKey(year, monthIdx+1);
  const noteText = months[key]?.targetNote || "";
  const [digitsDone, setDigitsDone] = useState(false);
  // Target-edit history modal — opened from the 📊 chart icon in the
  // card header. Shows the timeline of every target change written
  // through monthOps.updateTarget (which now appends to targetHistory).
  const [historyOpen, setHistoryOpen] = useState(false);

  // Compute modifier impact for this month
  const modImpact = useMemo(() => {
    if (!modifiers?.length) return null;
    let totalPct = 0;
    for (const m of modifiers) {
      if (m.type === "units" && m.monthKeys?.includes(key)) totalPct += Math.round((m.value||0)*100);
    }
    return totalPct || null;
  }, [modifiers, key]);
  const digits = String(months[mkKey(year, monthIdx+1)]?.target || 0).split("").length;
  const totalAnimMs = (animDelay || 0) + digits * 55 + 500;

  // When animKey changes (tab re-selected), reset and re-run animation once
  useEffect(() => {
    setDigitsDone(false);
    const t = setTimeout(() => setDigitsDone(true), totalAnimMs);
    return () => clearTimeout(t);
  }, [animKey]);
  const rawTarget = months[key]?.target || 0;
  const _rawActualSold = months[key]?.actualSold || 0;
  const isSingleMkt = Array.isArray(selMkts) && selMkts.length===1;
  const selectedMktId = isSingleMkt ? selMkts[0] : null;
  const selectedMkt = selectedMktId ? marketplaces.find(m=>m.id===selectedMktId) : null;
  const mktPct = selectedMkt ? (selectedMkt.pctOfTotal||0) : 1;
  const mktTargetKey = selectedMktId ? `mktTarget_${selectedMktId}` : null;
  const mktTargetOverride = mktTargetKey ? (months[key]?.[mktTargetKey]??null) : null;
  const expectedMktUnits = Math.round(rawTarget*mktPct);
  const hasManualEdit = isSingleMkt && mktTargetOverride!==null;

  // The "selection percent" — what fraction of the brand does the current
  // selection represent. Used to scale brand-wide values (actualSold, etc.)
  // when a single marketplace or a shared pool is selected. When "all" is
  // selected this is 1 (full brand). Same logic as displayTarget.
  const selPct = isSingleMkt ? mktPct : isPoolSelection ? poolPct : 1;

  // displayTarget resolution:
  //   1. Single marketplace → that marketplace's share (with manual override if set)
  //   2. Shared pool selected → combined pctOfTotal of all pool members × raw target
  //   3. All → brand target PLUS deltas from marketplace-level overrides
  //      (if Amazon was expected 600 but user set 800, the +200 rolls up)
  const mktOverrideDelta = useMemo(() => {
    if (isSingleMkt || isPoolSelection) return 0;
    let delta = 0;
    const md = months[key] || {};
    for (const mkt of marketplaces) {
      const ov = md[`mktTarget_${mkt.id}`];
      if (ov != null) {
        const expected = Math.round(rawTarget * (mkt.pctOfTotal || 0));
        delta += ov - expected;
      }
    }
    return delta;
  }, [isSingleMkt, isPoolSelection, months, key, marketplaces, rawTarget]);

  const displayTarget = isSingleMkt
    ? (hasManualEdit ? mktTargetOverride : expectedMktUnits)
    : isPoolSelection
      ? Math.round(rawTarget * poolPct)
      : rawTarget + mktOverrideDelta;

  // Actual sold scaled to the selection. months[].actualSold is brand-wide
  // (single number). Per-marketplace actuals require API-level granularity
  // we don't have yet, so we approximate: brand actualSold × selection %.
  // This gives a reasonable estimate that's consistent with how targets are
  // displayed for the same selection.
  const actualSold = Math.round(_rawActualSold * selPct);

  const deviationPct = hasManualEdit&&expectedMktUnits>0 ? Math.round(((mktTargetOverride-expectedMktUnits)/expectedMktUnits)*100) : 0;

  function getBatchInStockDate(batch) {
    if (batch.inStockMode==="date"&&batch.inStockDate) { const d=parseDate(batch.inStockDate); if(d) return d; }
    if (batch.arrivalDate) { const a=parseDate(batch.arrivalDate); if(a){ const days=batch.inStockDays??0; return new Date(a.getTime()+days*86400000); } }
    return null;
  }

  const stockEnteringMonth = useMemo(() => {
    if (!showDanger) return 0;
    const [y,m] = key.split("-").map(Number);
    // Filter orders by marketplace selection — only orders tagged to
    // the selected marketplace(s) contribute to stock for this view.
    // Untagged orders are excluded (mandatory-marketplace policy).
    const selectedIds = new Set(
      isSingleMkt && selectedMktId ? [selectedMktId]
      : isPoolSelection && selMkts ? selMkts
      : []
    );
    const filtOrders = selectedIds.size > 0
      ? (orders||[]).filter(o => {
          const linked = o.marketplaceIds || [];
          if (linked.length === 0) return false;
          return linked.some(id => selectedIds.has(id));
        })
      : (orders||[]);
    const batchByMonth = {};
    for (const order of filtOrders) {
      const totalCtForOrder = (order.containerLines||[]).reduce((s,l)=>s+(l.qty||0),0);
      for (const batch of order.containerBatches||[]) {
        const inStockDate = getBatchInStockDate(batch);
        if (!inStockDate) continue;
        const batchKey = `${inStockDate.getFullYear()}-${String(inStockDate.getMonth()+1).padStart(2,"0")}`;
        let batchUnits = 0;
        if (batch.unitCount!==undefined&&batch.unitCount>0) batchUnits=batch.unitCount;
        else if (order.unitCount>0&&totalCtForOrder>0) batchUnits=Math.round((batch.containers||0)/totalCtForOrder*order.unitCount);
        batchByMonth[batchKey] = (batchByMonth[batchKey]||0)+batchUnits;
      }
    }
    // Start with initial stock — already marketplace/pool-resolved via prop
    let startingStock = initStock || 0;
    const allKeys = new Set([...Object.keys(batchByMonth), ...Object.keys(months).filter(k=>k<key)]);
    const sortedKeys = [...allKeys].filter(k=>k<key).sort();
    let balance = startingStock;
    const shrinkMult = 1 - (shrinkagePct||0)/100;
    // Use selPct to scale brand-wide targets to the marketplace/pool selection
    for (const k of sortedKeys) { balance+=Math.round((batchByMonth[k]||0)*shrinkMult); const sold=Math.round((months[k]?.target||0)*selPct); balance=Math.max(0,balance-sold); }
    // Add arrivals for this month (with shrinkage)
    balance+=Math.round((batchByMonth[key]||0)*shrinkMult);
    // For current month, only subtract consumed portion (days elapsed / days in month)
    const today = new Date();
    if (y === today.getFullYear() && m === today.getMonth()+1) {
      const dim = new Date(y, m, 0).getDate();
      const consumed = Math.round(Math.round((months[key]?.target||0)*selPct) * today.getDate() / dim);
      balance = Math.max(0, balance - consumed);
    }
    return balance;
  }, [orders,months,key,showDanger,marketplaces,shrinkagePct,selPct,selMkts]);

  const hasOrderUnits = useMemo(()=>(orders||[]).some(o=>(o.containerBatches||[]).some(b=>getBatchInStockDate(b)!==null)),[orders]);
  // dangerPct = threshold %. E.g. 10% means red when stock is below 10% of target
  const dangerThreshold = displayTarget>0 ? Math.ceil(displayTarget*dangerPct/100) : 0;
  // Two-tier severity: critical (below threshold) vs warning (below target but above threshold)
  const stockSeverity = !showDanger || !hasOrderUnits || displayTarget <= 0 ? null
    : stockEnteringMonth < dangerThreshold ? "critical"   // red — stock below threshold %
    : stockEnteringMonth < displayTarget ? "warning"       // orange — below target but above threshold
    : null;                                                // green — stock covers target
  const stockAlert = stockSeverity !== null;

  // Revenue & inventory for this month — uses actualSold when available
  // (real payout based on what actually shipped) and falls back to the
  // user's target × weighted payout for projection. For the current month
  // we run-rate the elapsed days and project the remaining days.
  const _today = new Date();
  const _isPastMonth = year < _today.getFullYear() || (year === _today.getFullYear() && monthIdx < _today.getMonth());
  const _isCurrentMonth = year === _today.getFullYear() && monthIdx === _today.getMonth();
  let monthRevenue = 0;
  let revenueSource = "projected"; // "real" | "real_partial" | "projected" | "none"
  if (weightedPayout > 0) {
    if (_isPastMonth && actualSold > 0) {
      // Past month with real data → real payout
      monthRevenue = Math.round(actualSold * weightedPayout);
      revenueSource = "real";
    } else if (_isCurrentMonth && actualSold > 0) {
      // Current month: actualSold for elapsed days + run-rate projection for the rest.
      const dim = new Date(year, monthIdx + 1, 0).getDate();
      const dayOfMonthLocal = _today.getDate();
      const runRate = actualSold / Math.max(1, dayOfMonthLocal);
      const projectedRemaining = Math.round(runRate * Math.max(0, dim - dayOfMonthLocal));
      monthRevenue = Math.round((actualSold + projectedRemaining) * weightedPayout);
      revenueSource = "real_partial";
    } else if (displayTarget > 0) {
      monthRevenue = Math.round(displayTarget * weightedPayout);
      revenueSource = "projected";
    }
  }
  const inventoryValue = stockEnteringMonth > 0 && costPerUnit > 0 ? Math.round(stockEnteringMonth * costPerUnit) : 0;
  // Capacity warning: target exceeds available stock
  const exceedsStock = showDanger && stockEnteringMonth > 0 && displayTarget > 0 && displayTarget > stockEnteringMonth;
  const stockDeficit = exceedsStock ? displayTarget - stockEnteringMonth : 0;

  function resetManualEdit() {
    if (!mktTargetKey) return;
    // updateMonth spreads the result INTO prev, so we can't delete via functional patch.
    // Instead set the override to undefined — displayTarget falls back to expectedMktUnits.
    monthOps.updateMonth(key, { [mktTargetKey]: undefined });
  }
  function handleTargetChange(val) {
    setLastEditedKey(key);
    if (isSingleMkt&&selectedMktId) { const numVal=parseInt(val)||0; monthOps.updateMonth(key, { [mktTargetKey]:numVal }); }
    else monthOps.updateTarget(key,val);
  }

  // Local editing state — allows backspace to clear, prevents leading zeros
  const [showActualPrompt, setShowActualPrompt] = useState(false);
  const prevActualRef = useRef(months[key]?.actualSold || 0);
  const [editing, setEditing] = useState(false);
  const [localVal, setLocalVal] = useState("");

  function onFocus() {
    setEditing(true);
    setLocalVal(displayTarget > 0 ? String(displayTarget) : "");
  }
  function onBlur() {
    setEditing(false);
    const num = parseInt(localVal) || 0;
    if (num !== displayTarget) handleTargetChange(String(num));
  }
  function onLocalChange(e) {
    const raw = e.target.value;
    // Strip leading zeros: "080" → "80"
    const cleaned = raw.replace(/^0+(\d)/, "$1");
    setLocalVal(cleaned);
    // Also save immediately
    const num = parseInt(cleaned) || 0;
    handleTargetChange(String(num));
  }

  const TODAY = new Date();
  const isCurrentMonth = year===TODAY.getFullYear()&&monthIdx+1===TODAY.getMonth()+1;
  const isFutureYear = year > currentYear;
  const isFutureMonth = !isCurrentMonth && !isPrev && year === currentYear && monthIdx + 1 > TODAY.getMonth() + 1;

  const accentColor = stockSeverity==="critical" ? T.red : stockSeverity==="warning" ? T.orange : hasManualEdit ? T.orange : isCurrentMonth ? T.green : T.border;
  const targetColor = stockSeverity==="critical" ? T.red : stockSeverity==="warning" ? T.orange : hasManualEdit ? T.orange : isCurrentMonth ? T.green : T.text;

  return (
    <div style={{
      background: T.card,
      border:`1.5px solid ${accentColor}`,
      borderRadius:RADIUS.lg,
      overflow:"hidden",
      boxSizing:"border-box",
      transition:`border-color 0.25s ${SPRING}, box-shadow 0.25s, height 0.3s ease`,
      boxShadow: stockSeverity==="critical" ? undefined : stockSeverity==="warning" ? undefined : isCurrentMonth ? `0 0 0 2px ${T.green}33, 0 4px 20px rgba(0,0,0,0.1)` : "0 2px 8px rgba(0,0,0,0.06)",
      animation: stockSeverity==="critical" ? "siriGlow 2.4s linear infinite" : stockSeverity==="warning" ? "orangeGlow 3s linear infinite" : "none",
      position:"relative",
    }}>
      {/* Top accent line */}
      <div style={{ height:2, background: stockSeverity==="critical" ? T.red : stockSeverity==="warning" ? T.orange : hasManualEdit ? T.orange : isCurrentMonth ? T.green : isPrev ? T.orange+"66" : "transparent", transition:`background 0.25s ${SPRING}`, animation: stockSeverity==="critical" ? "sfPulse 2s ease infinite" : "none" }}/>

      <div style={{ padding:"5px 10px 6px" }}>
        {/* Month label row */}
        <div style={{ ...rowBetween(8), marginBottom:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:5 }}>
            <span style={{ fontSize:11, fontWeight:700, color:T.textSub, letterSpacing:0.6, textTransform:"uppercase" }}>{MONTH_NAMES[monthIdx].slice(0,3)}</span>
            {isCurrentMonth && <span style={{ width:6, height:6, borderRadius:"50%", background:T.green, display:"inline-block", animation:"sfDotPulse 2s ease infinite" }}/>}
          </div>
          <div style={{ display:"flex", gap:4, alignItems:"center" }}>
            {/* stock icons moved to top-right corner with note icon */}
          </div>
        </div>

        {/* Target number — input always visible, digit animation overlays and fades */}
        <div style={{ position:"relative" }}>
          <input type="number" value={editing ? localVal : (displayTarget || "")} placeholder="0"
            data-field-key={isSingleMkt && selectedMktId ? `month.${key}.mktTarget_${selectedMktId}` : `month.${key}.target`}
            onChange={onLocalChange}
            onFocus={onFocus}
            onBlur={onBlur}
            readOnly={can && !can("targets","edit")}
            className="sf-editable-num"
            style={{ fontFamily:FONT, fontSize:18, fontWeight:800,
              color: targetColor,
              padding:"0px 4px", width:"100%", textAlign:"center", boxSizing:"border-box",
              letterSpacing:-0.5, background:"transparent", border:"none", outline:"none",
              WebkitAppearance:"none",
              position:"relative", zIndex:1 }}/>
          {!digitsDone && displayTarget > 0 && (
            <div aria-hidden style={{
              position:"absolute", inset:0, display:"flex", alignItems:"center",
              justifyContent:"center", zIndex:2,
              background:T.card,
            }}>
              {String(displayTarget).split("").map((d, i) => (
                <span key={i} style={{
                  fontFamily:FONT, fontSize:18, fontWeight:800, color: targetColor,
                  display:"inline-block", letterSpacing:-0.5,
                  animation:`sfDigitUp 0.45s cubic-bezier(0.22,1,0.36,1) both`,
                  animationDelay:`${animDelay + i * 55}ms`,
                }}>{d}</span>
              ))}
            </div>
          )}
        </div>
        <SaveBadge status={saveStatus} show={lastEditedKey===key}/>

        {/* Confirm prompt — recalculate chart with actual sold?
            (Triggered from the Sold chip's hidden input below.) */}
        <ConfirmSheet
          open={showActualPrompt}
          title="Recalculate cashflow?"
          message={`Use your real sales data (${actualSold} units) to recalculate the chart for 1 ${MONTH_NAMES[monthIdx]} – ${TODAY.getDate()} ${MONTH_NAMES[TODAY.getMonth()]}?\n\n${isCurrentMonth
            ? `The remaining days will be projected from your current run rate (${Math.round(actualSold / Math.max(1, TODAY.getDate()))} units/day).`
            : `This will replace the target (${displayTarget} units) with actual performance for this month.`}`}
          confirmLabel="Yes, recalculate"
          danger={false}
          onConfirm={()=>{ monthOps.updateMonth(key, { useActualSold: true }); setShowActualPrompt(false); }}
          onCancel={()=>setShowActualPrompt(false)}
        />

        {/* ── Data chips (Phase 8 redesign) ─────────────────────────────
            Replaces the old tight "Sold: 265 / Projected: $17,408 / Real:
            $15,377" text rows with a stack of equal-size colored chips:
              [SOLD       │ 265        units]
              [PROJECTED  │ $17,408    ]
              [REAL       │ $15,377    ]
              [STOCK      │ 1,200 in stock]
            Label and value are vertically aligned across every chip so
            the column reads cleanly. Chip width is sized to fit values
            up to $1,000,000 without truncation. */}
        {!isMobileCard && (() => {
          const showSold = isPrev || isCurrentMonth || (year===currentYear && monthIdx+1<=TODAY.getMonth()+1);
          const projectedRevenue = displayTarget > 0 && weightedPayout > 0 ? Math.round(displayTarget * weightedPayout) : 0;
          const realRevenue = actualSold > 0 && weightedPayout > 0 ? Math.round(actualSold * weightedPayout) : 0;
          const showStock = showDanger && hasOrderUnits && displayTarget>0;
          if (!showSold && !projectedRevenue && !realRevenue && !showStock) return null;

          // Common chip chrome — pinned width so values always fit and
          // every chip lines up vertically. Label fixed at 38 px wide
          // so the label column is identical across every row.
          const Chip = ({ label, value, tooltip, color, onClick, children }) => (
            <Tooltip content={tooltip || ""}>
              <div onClick={onClick}
                style={{
                  display:"flex", alignItems:"center", gap: 8,
                  padding:"6px 10px",
                  background: color + "14",
                  border: `1px solid ${color}55`,
                  borderRadius: RADIUS.md,
                  fontFamily: FONT,
                  cursor: onClick ? "pointer" : "default",
                }}>
                <span style={{
                  fontSize: 9, fontWeight: 700, color: T.textSub,
                  letterSpacing: 0.4, textTransform: "uppercase",
                  width: 56, flexShrink: 0, textAlign: "left",
                }}>{label}</span>
                <span style={{
                  fontSize: 13, fontWeight: 800, color,
                  fontVariantNumeric: "tabular-nums",
                  flex: 1, textAlign: "right", whiteSpace: "nowrap",
                  overflow: "hidden", textOverflow: "ellipsis",
                }}>{value}</span>
                {children}
              </div>
            </Tooltip>
          );

          return (
            <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
              {showSold && (
                <Chip
                  label="Sold"
                  value={actualSold > 0 ? `${actualSold.toLocaleString()}` : "—"}
                  color={actualSold > 0 ? (actualSold >= displayTarget ? T.green : T.red) : T.textMuted}
                  tooltip="Units sold this month. Enter the actual number; green ≥ target, red below."
                  onClick={(e) => {
                    e.stopPropagation();
                    // Surface the editable input via a hidden focus shim
                    // below. Clicking the chip moves focus into it.
                    const el = e.currentTarget.querySelector("input");
                    if (el) el.focus();
                  }}>
                  <input
                    type="number"
                    value={months[key]?.actualSold || ""}
                    placeholder=""
                    onChange={e => { setLastEditedKey(key); monthOps.updateActualSold(key, e.target.value); }}
                    onBlur={() => {
                      const cur = months[key]?.actualSold || 0;
                      const prev = prevActualRef.current;
                      if (cur > 0 && cur !== prev && !months[key]?.useActualSold) setShowActualPrompt(true);
                      prevActualRef.current = cur;
                    }}
                    readOnly={can && !can("targets","edit")}
                    style={{
                      // Visually invisible — the chip's value span shows the
                      // formatted number. This input owns the editable state.
                      position:"absolute", opacity: 0, pointerEvents: "none",
                      width: 1, height: 1,
                    }}/>
                </Chip>
              )}
              {projectedRevenue > 0 && (
                <Chip
                  label="Projected"
                  value={`${currSym()}${projectedRevenue.toLocaleString()}`}
                  color={T.green}
                  tooltip={`Projected revenue: ${displayTarget.toLocaleString()} units × ${currSym()}${weightedPayout.toFixed(2)} payout`}/>
              )}
              {realRevenue > 0 && (
                <Chip
                  label="Real"
                  value={`${currSym()}${realRevenue.toLocaleString()}`}
                  color={T.accent}
                  tooltip={`Realised revenue: ${actualSold.toLocaleString()} actual units × ${currSym()}${weightedPayout.toFixed(2)} payout`}/>
              )}
              {showStock && (
                <Chip
                  label="Stock"
                  value={stockEnteringMonth > 0 ? `${stockEnteringMonth.toLocaleString()} units` : "No stock"}
                  color={stockSeverity === "critical" ? T.red
                       : stockSeverity === "warning" ? T.orange
                       : T.textMuted}
                  tooltip={stockEnteringMonth > 0
                    ? `${stockEnteringMonth} units in stock entering this month (target ${displayTarget})`
                    : "No stock entering this month"}/>
              )}
            </div>
          );
        })()}

        {/* Units left, capacity warning, modifier impact, balance — hidden in compact 2-row layout (info still in tooltips on top-right icons) */}

        {/* Top-right icons: stock alerts + chart icon + note icon */}
        {!isMobileCard && (
          <div style={{ position:"absolute", top:4, right:4, display:"flex", gap:3, alignItems:"center" }}>
            {stockSeverity==="critical" && (
              <Tooltip content={`⚠ Stock critically low: ${stockEnteringMonth} units projected vs ${dangerThreshold} threshold (${dangerPct}% of ${displayTarget} target).`}>
              <span style={{ fontSize:13, color:T.red, cursor:"help", animation:"sfPulse 2s ease infinite", fontWeight:700, textShadow:`0 0 8px ${T.red}` }}>⚠</span>
              </Tooltip>
            )}
            {stockSeverity==="warning" && (
              <Tooltip content={`📦 Stock below target: ${stockEnteringMonth} units projected vs ${displayTarget} target. Above critical threshold (${dangerThreshold}).`}>
              <span style={{ fontSize:13, color:T.orange, cursor:"help", fontWeight:700, textShadow:`0 0 6px ${T.orange}` }}>📦</span>
              </Tooltip>
            )}
            {/* Target-edit history chart — every change to the target
                input writes a timeline entry; click to view. */}
            {(() => {
              const hist = months[key]?.targetHistory || [];
              const hasHist = hist.length > 0;
              return (
                <Tooltip content={hasHist
                  ? `View the ${hist.length} change${hist.length === 1 ? "" : "s"} to this month's target`
                  : "No edits yet — target changes will be recorded here"}>
                  <button onClick={e => { e.stopPropagation(); setHistoryOpen(true); }}
                    style={{
                      background: hasHist ? T.accent + "15" : "transparent",
                      border: `1px solid ${hasHist ? T.accent + "40" : "transparent"}`,
                      borderRadius: RADIUS.sm, cursor: "pointer", fontSize: 13,
                      padding: "2px 4px", color: hasHist ? T.accent : T.textSub,
                    }}>📊</button>
                </Tooltip>
              );
            })()}
            {((!can || can("targets","edit")) || noteText) && (
              <Tooltip content={noteText || "Add a monthly note"}>
              <button onClick={e=>{e.stopPropagation(); if(onEditNote) onEditNote(key, noteText);}}
                style={{ background:noteText?T.accent+"15":"transparent", border:`1px solid ${noteText?T.accent+"40":"transparent"}`, borderRadius:RADIUS.sm, cursor:"pointer", fontSize:13, opacity:1, padding:"2px 4px",
                  color: noteText ? T.accent : T.textSub }}>📝</button>
              </Tooltip>
            )}
          </div>
        )}

        {/* ── Target-history modal ──────────────────────────────────── */}
        {historyOpen && (
          <TargetHistoryModal
            T={T}
            monthLabel={`${MONTH_NAMES[monthIdx]} ${year}`}
            currentTarget={displayTarget}
            history={months[key]?.targetHistory || []}
            onClose={() => setHistoryOpen(false)}
          />
        )}

        {/* Balance hidden in compact 2-row layout */}

        {/* Simulator comparison — desktop only, always rendered for smooth transition.
            Two-column layout: row 1 (New Target | Payout Difference), row 2 (New Payout | New Balance) */}
        {!isMobileCard && !isPrev && displayTarget > 0 && (()=>{
          const active = simPct !== 0;
          const simTarget = Math.round(displayTarget * (1 + simPct/100));
          const origPayout = displayTarget * (weightedPayout||0);
          const simPayout = simTarget * (weightedPayout||0);
          const cashDelta = simPayout - origPayout;
          const simColor = simPct > 0 ? T.green : T.red;
          return (
            <div style={{ marginTop:active?4:0, padding:active?"4px 6px":"0 6px", background:active?simColor+"08":"transparent", borderRadius:RADIUS.sm, border:active?`1px solid ${simColor}25`:"none", maxHeight:active?200:0, opacity:active?1:0, overflow:"hidden", transition:"max-height 0.3s ease, opacity 0.25s ease, margin-top 0.3s ease, padding 0.3s ease" }}>
              {weightedPayout > 0 ? (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", rowGap:3, columnGap:6 }}>
                  {/* Row 1 col 1: New Target */}
                  <div style={{ fontSize:8, lineHeight:1.2 }}>
                    <div style={{ color:T.textSub }}>New Target</div>
                    <div style={{ color:simColor, fontWeight:800 }}>{simTarget.toLocaleString()}</div>
                  </div>
                  {/* Row 1 col 2: Payout Difference */}
                  <div style={{ fontSize:8, lineHeight:1.2 }}>
                    <div style={{ color:T.textSub }}>Payout Difference</div>
                    <div style={{ color:simColor, fontWeight:800 }}>{cashDelta>=0?"+":""}{currSym()}{Math.round(cashDelta).toLocaleString()}</div>
                  </div>
                  {/* Row 2 col 1: New Payout */}
                  <div style={{ fontSize:8, lineHeight:1.2 }}>
                    <div style={{ color:T.textSub }}>New Payout</div>
                    <div style={{ color:simColor, fontWeight:800 }}>{currSym()}{Math.round(simPayout).toLocaleString()}</div>
                  </div>
                  {/* Row 2 col 2: New Balance */}
                  <div style={{ fontSize:8, lineHeight:1.2 }}>
                    <div style={{ color:T.textSub }}>New Balance</div>
                    <div style={{ color:simMonthBalance>=0?T.green:T.red, fontWeight:800 }}>{currSym()}{Math.round(simMonthBalance).toLocaleString()}</div>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize:8, color:simColor, fontWeight:700, textAlign:"center" }}>New Target: {simTarget.toLocaleString()}</div>
              )}
            </div>
          );
        })()}

        {/* Modifier impact hidden in compact 2-row layout */}

        {/* Reset icon — top-right of card, icon only */}
        {!isMobileCard && hasManualEdit && (
          <div style={{ position:"absolute", top:6, right:6 }}>
            <Tooltip content={`Manually edited (${deviationPct>0?"+":""}${deviationPct}% vs expected). Click to reset to the auto-calculated value.`}>
            <button onClick={e=>{e.stopPropagation();resetManualEdit();}}
              style={{ fontSize:14, color:T.orange, background:T.orange+"15", border:`1px solid ${T.orange+"44"}`, borderRadius:RADIUS.sm, width:24, height:24, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontFamily:FONT, fontWeight:700, padding:0 }}>
              ↺
            </button>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Targets({ marketplaces, months, monthOps, visibleYears, saveStatus="saved", orders=[], decisions=[], modifiers=[], can, isOwner, initialStock=0, initialCostPerUnit=0, initialStockDate="", recurringIncome=[], recurringExpenses=[], deliveryCreditLimit=0, fbmSettings=[], partners=[], inventoryPools=[], poolOps }) {
  const T = useTheme();
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const TODAY = useMemo(()=>new Date(),[]);
  const dateFormat = getDateFormat(months?._appSettings);
  const currentYear = TODAY.getFullYear();

  // ── Product-level target filter ──────────────────────────────────────────
  // Only visible when a single marketplace is selected (not pool/all).
  // Selecting a product shows its per-month target breakdown alongside the
  // marketplace total.
  const [selProductId, setSelProductId] = useState(null);

  // Shrinkage state — must be declared before todayInventory which uses it
  const [shrinkagePct, setShrinkagePct] = useState(() => months._targetSettings?.shrinkagePct ?? 2);
  const [shrinkagePctStr, setShrinkagePctStr] = useState(() => String(months._targetSettings?.shrinkagePct ?? 2));
  function saveShrinkagePct(v) { setShrinkagePct(v); monthOps.updateMonth("_targetSettings", { ...(months._targetSettings||{}), shrinkagePct: v }); }

  // Auto-computed inventory from initial stock + orders - sales
  const todayInventory = useCached("tgt_todayInv", () => {
    const { currentStock } = computeCurrentStock({ initialStock, initialCostPerUnit, initialStockDate, orders, months, today: TODAY, shrinkagePct });
    return currentStock;
  }, [initialStock, initialCostPerUnit, initialStockDate, orders, months, TODAY, shrinkagePct]);

  // Weighted payout per unit (for revenue display on month cards)
  const weightedPayout = useMemo(() =>
    marketplaces.reduce((s, mkt) => s + (Number(mkt.pctOfTotal) || 0) * effectivePayoutForMkt(mkt), 0)
  , [marketplaces]);

  // Break-even for current month
  const breakeven = useCached("tgt_breakeven", () => {
    const curKey = mkKey(currentYear, TODAY.getMonth()+1);
    const allRec = [...recurringIncome, ...recurringExpenses, ...partners.map(p=>({...p,isIncome:false,type:p.type||"calendar"}))];
    const recItems = generateRecurringItems(allRec, visibleYears);
    const delExp = computeDeliveryExpenses(months, marketplaces, deliveryCreditLimit, fbmSettings);
    const ordExp = generateOrderExpenses(orders);
    const invSummary = { weightedCostPerUnit: initialCostPerUnit || 0 };
    return computeBreakevenUnits(curKey, marketplaces, months, delExp, ordExp, recItems, { fbmSettings, inventorySummary: invSummary, today: TODAY, bufferThreshold: Number(months?._appSettings?.bufferThreshold) || 0 });
  }, [currentYear, TODAY, marketplaces, months, orders, recurringIncome, recurringExpenses, partners, visibleYears, deliveryCreditLimit, fbmSettings, initialCostPerUnit]);

  // Stockout projection
  const stockoutInfo = useMemo(() =>
    computeStockoutDate({ currentStock: todayInventory, orders, months, today: TODAY, shrinkagePct })
  , [todayInventory, orders, months, TODAY, shrinkagePct]);

  // Stockout buffer (days) and custom lead time (days)
  const [stockoutBuffer, setStockoutBuffer] = useState(()=>{ try { return parseInt(localStorage.getItem("sf_t_soBuffer"))||0; } catch { return 0; } });
  useEffect(()=>{ try { localStorage.setItem("sf_t_soBuffer",String(stockoutBuffer)); } catch {} },[stockoutBuffer]);
  const [customLeadTime, setCustomLeadTime] = useState(()=>{ try { return parseInt(localStorage.getItem("sf_t_custLead"))||0; } catch { return 0; } });
  useEffect(()=>{ try { localStorage.setItem("sf_t_custLead",String(customLeadTime)); } catch {} },[customLeadTime]);
  // Mirror customLeadTime to the server so the alerts cron uses the same
  // number. Debounced 800ms to avoid a POST per keystroke. Owner-only on
  // the server — members get a 403 we silently ignore.
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { supabase } = await import("../hooks/useData.js");
        const tok = (await supabase.auth.getSession()).data?.session?.access_token;
        if (!tok) return;
        await fetch("/api/account", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
          body: JSON.stringify({ action: "set_lead_time", days: customLeadTime || null }),
        });
      } catch {}
    }, 800);
    return () => clearTimeout(t);
  }, [customLeadTime]);

  // Lead time, effectiveLeadTime, avgLeadTime — declared after marketplace
  // selection variables (isSingleMkt, isPoolSelection) so lead time is
  // filtered per channel. See below (~line 700+).

  // Inventory summary (days of stock, payout delay, etc.)
  const invSummary = useCached("tgt_invSummary", () =>
    computeInventorySummary(marketplaces, months, TODAY, initialStock, initialCostPerUnit, initialStockDate, orders, shrinkagePct)
  , [marketplaces, months, TODAY, initialStock, initialCostPerUnit, initialStockDate, orders, shrinkagePct]);

  // Sell-through rate: units sold in trailing ~90 days / (sold + current stock)
  const sellThrough = useMemo(() => {
    const cm = TODAY.getMonth(); // 0-based
    const cy = TODAY.getFullYear();
    let sold = 0;
    for (let i = 0; i < 3; i++) {
      let m = cm - i; let y = cy;
      if (m < 0) { m += 12; y--; }
      const k = mkKey(y, m + 1);
      sold += months[k]?.actualSold || 0;
    }
    const total = sold + todayInventory;
    return { sold, pct: total > 0 ? Math.round(sold / total * 100) : 0 };
  }, [months, TODAY, todayInventory]);

  // Target simulator state
  const [simOpen, setSimOpen] = useState(false);
  const [simPct, setSimPct] = useState(0);
  const [simMode, setSimMode] = useState("all"); // "all" | "custom"
  const [simCustom, setSimCustom] = useState({}); // { "2026-05": 15 }
  const [simSelectedMonths, setSimSelectedMonths] = useState([]); // month indices [0..11]

  // Launch risk state
  const [launchOpen, setLaunchOpen] = useState(false);
  const [launchOrderIdx, setLaunchOrderIdx] = useState(0);
  const [launchMonths, setLaunchMonths] = useState([]); // month indices
  const [launchUnitsPerMonth, setLaunchUnitsPerMonth] = useState({});
  const [launchAccountPayout, setLaunchAccountPayout] = useState(false);

  // Decision impact toggle — which decisions are active on targets
  const [decImpactOn, setDecImpactOn] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sf_t_decImpact")) || {}; } catch { return {}; }
  });
  useEffect(() => { try { localStorage.setItem("sf_t_decImpact", JSON.stringify(decImpactOn)); } catch {} }, [decImpactOn]);

  // ── Persistent state — survives page switches ──
  const lsg = (k,d) => { try { return localStorage.getItem(k)||d; } catch { return d; }};
  const lsj = (k,d) => { try { return JSON.parse(localStorage.getItem(k))||d; } catch { return d; }};
  const lss = (k,v) => { try { localStorage.setItem(k,typeof v==="object"?JSON.stringify(v):String(v)); } catch {} };

  // KPI range toggle: "today" = from current date, "jan" = from Jan 1
  const [kpiFrom, setKpiFrom] = useState(()=>lsg("sf_t_kpiFrom","today"));
  useEffect(()=>lss("sf_t_kpiFrom",kpiFrom),[kpiFrom]);

  // Rolling 12-month window
  const viewMonths = useMemo(() => Array.from({length:12}, (_,i) => {
    if (kpiFrom === "jan") return { year: currentYear, month: i };
    let m = TODAY.getMonth() + i;
    let y = currentYear;
    if (m > 11) { m -= 12; y++; }
    return { year: y, month: m };
  }), [kpiFrom, currentYear, TODAY]);

  const [expandedYears, setExpandedYears] = useState(()=>lsj("sf_t_expYears",Object.fromEntries(visibleYears.map(y=>[y,y<=currentYear]))));
  const [lastEditedKey, setLastEditedKey] = useState(null);
  const [selMkts, setSelMkts] = useState(()=>lsg("sf_t_selMkts","all"));
  const [chartYear, setChartYear] = useState(()=>parseInt(lsg("sf_t_chartYear",currentYear)));
  const [copyConfirm, setCopyConfirm] = useState(null);
  const [copyGrowth, setCopyGrowth] = useState(()=>parseInt(lsg("sf_t_copyGrowth","0")));
  const [noteTarget, setNoteTarget] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [chartView, setChartView] = useState(()=>lsg("sf_t_chartView","month"));
  const [showInventory, setShowInventory] = useState(()=>lsg("sf_t_showInv","false")==="true");
  const [showOrders, setShowOrders] = useState(()=>lsg("sf_t_showOrd","false")==="true");
  const [selectedOrderId, setSelectedOrderId] = useState(()=>lsg("sf_t_selOrder","all"));
  const [orderDropOpen, setOrderDropOpen] = useState(false);
  const orderDropRef = useRef(null);
  useEffect(()=>{
    if (!orderDropOpen) return;
    const h = e => { if (orderDropRef.current && !orderDropRef.current.contains(e.target)) setOrderDropOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  },[orderDropOpen]);
  const [hoveredDiamond, setHoveredDiamond] = useState(null);
  const chartContainerRef = useRef(null);
  const whatIfRef = useRef(null);
  const [chartH, setChartH] = useState(300);
  const [chartFullscreen, setChartFullscreen] = useState(false);
  useEffect(() => {
    if (!chartFullscreen) return;
    const onKey = e => { if (e.key === "Escape") setChartFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chartFullscreen]);
  const calcChartH = useCallback(() => {
    if (chartContainerRef.current) {
      const rect = chartContainerRef.current.getBoundingClientRect();
      const available = window.innerHeight - rect.top - 40;
      setChartH(Math.max(200, available));
    }
  }, []);
  useEffect(() => {
    // Recalculate after layout settles
    const t = setTimeout(calcChartH, 50);
    window.addEventListener("resize", calcChartH);
    return () => { clearTimeout(t); window.removeEventListener("resize", calcChartH); };
  }, [calcChartH, simOpen, launchOpen]); // recalc when panels open/close

  // Persist on change
  useEffect(()=>lss("sf_t_expYears",expandedYears),[expandedYears]);
  useEffect(()=>lss("sf_t_selMkts",selMkts),[selMkts]);
  useEffect(()=>lss("sf_t_chartYear",chartYear),[chartYear]);
  useEffect(()=>lss("sf_t_copyGrowth",copyGrowth),[copyGrowth]);
  useEffect(()=>lss("sf_t_chartView",chartView),[chartView]);
  useEffect(()=>lss("sf_t_showInv",showInventory),[showInventory]);
  useEffect(()=>lss("sf_t_showOrd",showOrders),[showOrders]);
  useEffect(()=>lss("sf_t_selOrder",selectedOrderId),[selectedOrderId]);

  // ── Digit animation — new key fires once per tab visit ──────────────────────
  const [animKey, setAnimKey] = useState(()=>Date.now());
  // Each card gets a random base delay 0–600ms, seeded per animKey so consistent within a run
  const animDelays = useMemo(() => {
    const delays = {};
    visibleYears.forEach(y => {
      for (let m = 0; m < 12; m++) {
        delays[`${y}_${m}`] = Math.floor(Math.random() * 600);
      }
    });
    return delays;
  }, [animKey]);

  // Re-trigger animation when component mounts (tab selected)
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) setAnimKey(Date.now());
    mountedRef.current = true;
  }, []);
  // Multi-select toggle — matches Dashboard pattern. Click a marketplace to
  // add/remove it from the selection. When the selection becomes empty, snap
  // back to "all" so the page never displays nothing.
  function toggleMkt(id) {
    setSelProductId(null); // Reset product filter on marketplace change
    if (selMkts === "all" || !Array.isArray(selMkts)) { setSelMkts([id]); return; }
    if (selMkts.includes(id)) {
      const next = selMkts.filter(x => x !== id);
      setSelMkts(next.length ? next : "all");
    } else {
      setSelMkts([...selMkts, id]);
    }
  }
  const [showDanger, setShowDanger] = useState(() => months._targetSettings?.showDanger ?? LS.getBool(LS.TARGETS_DANGER_ON, false));
  const [dangerPct, setDangerPct]   = useState(() => months._targetSettings?.dangerPct ?? LS.getFloat(LS.TARGETS_DANGER_PCT, 10));
  const [dangerPctStr, setDangerPctStr] = useState(() => String(months._targetSettings?.dangerPct ?? LS.getFloat(LS.TARGETS_DANGER_PCT, 10)));
  function saveDangerOn(v)  { setShowDanger(v); LS.setBool(LS.TARGETS_DANGER_ON, v); monthOps.updateMonth("_targetSettings", { ...(months._targetSettings||{}), showDanger: v, dangerPct }); }
  function saveDangerPct(v) { setDangerPct(v);  LS.setFloat(LS.TARGETS_DANGER_PCT, v); monthOps.updateMonth("_targetSettings", { ...(months._targetSettings||{}), dangerPct: v, showDanger }); }
  const isSingleMkt = Array.isArray(selMkts)&&selMkts.length===1;
  const selectedMkt = isSingleMkt?marketplaces.find(m=>m.id===selMkts[0]):null;
  const selectedMktIdx = selectedMkt?marketplaces.indexOf(selectedMkt):-1;
  const selectedMktColor = selectedMktIdx>=0?MKT_COLORS[selectedMktIdx%MKT_COLORS.length]:T.accent;
  // Detect shared-pool selection: the user picked 2+ marketplace IDs that
  // all belong to exactly one shared pool. When true, the display shows
  // the combined pool target (sum of pctOfTotal for pool members × raw)
  // instead of the brand-wide total or a single marketplace's share.
  const selectedPool = useMemo(() => {
    if (!Array.isArray(selMkts) || selMkts.length < 2) return null;
    return (inventoryPools || []).find(p =>
      p.isShared
      && Array.isArray(p.marketplaceIds)
      && p.marketplaceIds.length === selMkts.length
      && p.marketplaceIds.every(id => selMkts.includes(id))
    ) || null;
  }, [selMkts, inventoryPools]);
  const isPoolSelection = !!selectedPool;
  // Pool combined pctOfTotal — sum of each member marketplace's share
  const poolPct = useMemo(() => {
    if (!selectedPool) return 1;
    return (selectedPool.marketplaceIds || []).reduce((s, id) => {
      const mkt = marketplaces.find(m => m.id === id);
      return s + (Number(mkt?.pctOfTotal) || 0);
    }, 0);
  }, [selectedPool, marketplaces]);
  // Top-level selection percent — what fraction of the brand does the current
  // marketplace/pool selection represent. Used by MonthCards, chart, summary
  // rows, and KPI cards to scale brand-wide values (actualSold, etc.).
  const _selPct = isSingleMkt && selectedMkt ? (selectedMkt.pctOfTotal||0) : isPoolSelection ? poolPct : 1;

  // Shared target resolver — respects per-marketplace manual overrides.
  // Used by KPI cards, chart, and summary rows so they all show identical numbers.
  const resolveTargetForKey = useCallback((mKey) => {
    const raw = months[mKey]?.target || 0;
    if (isSingleMkt && selectedMkt) {
      const ov = months[mKey]?.[`mktTarget_${selectedMkt.id}`];
      return ov != null ? ov : Math.round(raw * (selectedMkt.pctOfTotal || 0));
    }
    if (isPoolSelection) return Math.round(raw * poolPct);
    return raw;
  }, [months, isSingleMkt, selectedMkt, isPoolSelection, poolPct]);

  // Lead time from past orders, filtered by marketplace/pool selection so
  // each channel gets its own lead time instead of a brand-wide average.
  const leadTimeInfo = useMemo(() => {
    const selectedIds = new Set(
      isSingleMkt && selectedMkt ? [selectedMkt.id]
      : isPoolSelection && Array.isArray(selMkts) ? selMkts
      : []
    );
    const filteredOrders = selectedIds.size > 0
      ? (orders || []).filter(o => {
          const linked = o.marketplaceIds || [];
          return linked.length === 0 || linked.some(id => selectedIds.has(id));
        })
      : orders;
    const agg = aggregateLeadTimes(filteredOrders);
    return {
      lastOrder: agg.last ?? agg.avg ?? 90,
      avg: agg.avg ?? 90,
      min: agg.min ?? 90,
      max: agg.max ?? 90,
      count: agg.count,
    };
  }, [orders, isSingleMkt, selectedMkt, isPoolSelection, selMkts]);
  const effectiveLeadTime = customLeadTime > 0 ? customLeadTime : leadTimeInfo.lastOrder;
  const avgLeadTime = { avg: effectiveLeadTime, count: leadTimeInfo.count, min: leadTimeInfo.min, max: leadTimeInfo.max };

  const hasOrderUnits = orders.some(o=>o.unitCount>0);

  // Compute stock arrivals from order batches for chart year. MUST be
  // declared AFTER isSingleMkt, selectedMkt, isPoolSelection, selMkts —
  // otherwise the production build throws "Cannot access before initialization."
  const stockArrivals = useMemo(()=>{
    const arrivals = [];
    // Include both viewMonths years AND chartYear so the chart's stock
    // projection sees arrivals regardless of which year is displayed.
    const viewYears = new Set([...viewMonths.map(vm => vm.year), chartYear]);
    const selectedIds = new Set(
      isSingleMkt && selectedMkt ? [selectedMkt.id]
      : isPoolSelection && Array.isArray(selMkts) ? selMkts
      : []
    );
    for (const order of (orders||[])) {
      if (selectedIds.size > 0) {
        const linked = order.marketplaceIds || [];
        if (linked.length === 0 || !linked.some(id => selectedIds.has(id))) continue;
      }
      for (const cb of (order.containerBatches||[])) {
        let inStockD = null;
        if (cb.inStockDate) inStockD = parseDate(cb.inStockDate);
        else if (cb.arrivalDate) {
          const arr = parseDate(cb.arrivalDate);
          if (arr) {
            const addDays = (cb.inStockMode||"days")==="days" ? (cb.inStockDays||0) : 0;
            inStockD = new Date(arr.getTime() + addDays*86400000);
          }
        }
        if (inStockD && viewYears.has(inStockD.getFullYear())) {
          const totalUnits = cb.unitCount || Math.round((order.unitCount||0) * (cb.containers||1) / Math.max(1,(order.containerBatches||[]).reduce((s,b)=>s+(b.containers||0),0)));
          arrivals.push({ month: inStockD.getMonth(), year: inStockD.getFullYear(), units: totalUnits, date: inStockD });
        }
      }
    }
    return arrivals.sort((a,b)=>a.date-b.date);
  }, [orders, viewMonths, chartYear, selMkts, isSingleMkt, selectedMkt, isPoolSelection]);

  // Pool-aware inventory: synthesizes a virtual aggregate pool that represents
  // the union of every pool touching the selection. Multi-mkt and "all" modes
  // get the COMBINED inventory instead of just the first pool (the bug that
  // made Targets show only Amazon when "all" was selected).
  const activePool = useMemo(() => {
    const selectedIds = (selMkts === "all" || !Array.isArray(selMkts) || !selMkts.length)
      ? marketplaces.map(m => m.id)
      : selMkts;
    if (inventoryPools.length > 0) {
      const synth = synthesizePoolForSelection(inventoryPools, selectedIds, marketplaces);
      if (synth) return synth;
      // Selected marketplace(s) not in any pool — fall through to virtual pool
    }
    if (selectedIds.length === 0) return null;
    return {
      id: "_virtual_no_pool",
      name: "Virtual aggregate",
      marketplaceIds: selectedIds,
      orderIds: [],
      initialStock: 0,
      initialStockDate: null,
      initialCostPerUnit: 0,
      stockOverridden: false,
      _synthetic: true,
    };
  }, [inventoryPools, selMkts, marketplaces]);

  // Pool-aware versions of inventory metrics (overrides global when pool exists).
  // Forwards `marketplaces` so the stock-resolution hierarchy
  // (pool override > per-marketplace cascade > pool baseline > legacy > 0)
  // and the COG hierarchy can resolve properly. Without `marketplaces`, the
  // helper would only see the pool baseline.
  const poolInventory = useMemo(() => {
    if (!activePool) return todayInventory;
    const { currentStock } = computeCurrentStock({ initialStock, initialCostPerUnit, initialStockDate, orders, months, today: TODAY, shrinkagePct, pool: activePool, marketplaces });
    return currentStock;
  }, [activePool, initialStock, initialCostPerUnit, initialStockDate, orders, months, TODAY, shrinkagePct, todayInventory, marketplaces]);

  const poolStockout = useMemo(() => {
    if (!activePool) return stockoutInfo;
    return computeStockoutDate({ currentStock: poolInventory, orders, months, today: TODAY, shrinkagePct, pool: activePool, targetScale: _selPct, resolveTarget: resolveTargetForKey });
  }, [activePool, poolInventory, orders, months, TODAY, shrinkagePct, stockoutInfo, _selPct]);

  const poolInvSummary = useMemo(() => {
    if (!activePool) return invSummary;
    return computeInventorySummary(marketplaces, months, TODAY, initialStock, initialCostPerUnit, initialStockDate, orders, shrinkagePct, activePool);
  }, [activePool, marketplaces, months, TODAY, initialStock, initialCostPerUnit, initialStockDate, orders, shrinkagePct, invSummary]);

  // Effective starting stock for the month-by-month walk in MonthCard.
  // Picks the pool-resolved value when a pool exists, else falls back to the
  // legacy global initialStock. This is what unifies Targets with Dashboard:
  // both now read the same hierarchy-resolved number.
  const effectiveStartingStock = activePool ? poolInventory : initialStock;

  // Sim-aware stockout — when the what-if simulator is open, compute
  // a stockout projection using the sim-modified targets. This value
  // flows into the main Stockout + Reorder KPI cards so they reflect
  // the "what if" scenario, not just the base projection.
  const simAwareStockout = useMemo(() => {
    if (!simOpen) return null; // sim off → use base stockoutInfo
    const inv = poolInventory || todayInventory || 0;
    if (inv <= 0) return { daysAway: 0, date: TODAY };
    let stk = inv;
    for (let d = 0; d < 365; d++) {
      const dt = new Date(TODAY.getTime() + d * 86400000);
      const mk = mkKey(dt.getFullYear(), dt.getMonth() + 1);
      const baseT = months[mk]?.target || 0;
      const pct = simMode === "all" ? simPct : (simCustom[mk] || 0);
      const simT = Math.round(baseT * (1 + pct / 100));
      const dim = getDaysInMonth(dt.getFullYear(), dt.getMonth() + 1);
      stk -= simT / dim;
      if (stk <= 0) return { daysAway: d, date: dt };
    }
    return null; // 365+ days
  }, [simOpen, simPct, simMode, simCustom, poolInventory, todayInventory, months, TODAY]);

  // Reorder alert — uses poolStockout (pool/marketplace-aware) so the
  // reorder deadline reflects the SELECTED marketplace(s), not brand-wide.
  // MUST be declared AFTER poolStockout (line ~735) to avoid "Cannot access
  // before initialization" in production builds.
  const reorderAlert = useMemo(() => {
    const leadTimeDays = avgLeadTime.avg;
    // When sim is open, prefer the sim-adjusted stockout so the reorder
    // deadline reflects the "what if" scenario.
    const soInfo = (simOpen && simAwareStockout !== undefined ? simAwareStockout : null) || poolStockout || stockoutInfo;
    if (!soInfo) {
      const farDate = new Date(TODAY.getTime() + 365 * 86400000);
      const orderByDate = new Date(farDate.getTime() - leadTimeDays * 86400000);
      const daysUntilOrderBy = Math.max(0, Math.floor((orderByDate.getTime() - TODAY.getTime()) / 86400000));
      return { stockoutDate: farDate, daysAway: 365, orderByDate, daysUntilOrderBy, needsReorder: false, latestArrival: null };
    }
    const stockoutMs = soInfo.date.getTime();
    // Filter orders by marketplace selection
    const selectedIds = new Set(
      isSingleMkt && selectedMkt ? [selectedMkt.id]
      : isPoolSelection && Array.isArray(selMkts) ? selMkts
      : []
    );
    let latestArrival = null;
    for (const o of orders) {
      if (selectedIds.size > 0) {
        const linked = o.marketplaceIds || [];
        if (linked.length > 0 && !linked.some(id => selectedIds.has(id))) continue;
      }
      for (const cb of (o.containerBatches || [])) {
        const d = parseDate(cb.arrivalDate);
        if (d && d > TODAY) {
          if (!latestArrival || d > latestArrival) latestArrival = d;
        }
      }
    }
    const orderByDate = new Date(stockoutMs - leadTimeDays * 86400000);
    const needsReorder = !latestArrival || latestArrival < soInfo.date;
    const daysUntilOrderBy = Math.max(0, Math.floor((orderByDate.getTime() - TODAY.getTime()) / 86400000));
    return { stockoutDate: soInfo.date, daysAway: soInfo.daysAway, orderByDate, daysUntilOrderBy, needsReorder, latestArrival };
  }, [poolStockout, stockoutInfo, simOpen, simAwareStockout, orders, TODAY, avgLeadTime, selMkts, isSingleMkt, selectedMkt, isPoolSelection]);

  return (
    <div style={{ fontFamily:FONT }}>
      <style>{`
  @keyframes sfPulse{0%,100%{opacity:1}50%{opacity:0.5}}
  @keyframes sfDotPulse{0%,100%{transform:scale(1);box-shadow:0 0 0 0 currentColor}50%{transform:scale(1.2);box-shadow:0 0 0 5px transparent}}
  @keyframes siriGlow{
    0%  {box-shadow:0 0 0 0px #FF3B3000, 0 0 0px 0px #FF3B3000}
    50% {box-shadow:0 0 0 3px #FF3B3066, 0 0 12px 4px #FF375F55}
    100%{box-shadow:0 0 0 0px #FF3B3000, 0 0 0px 0px #FF3B3000}
  }
  @keyframes orangeGlow{
    0%  {box-shadow:0 0 0 0px #FF950000, 0 0 0px 0px #FF950000}
    50% {box-shadow:0 0 0 2px #FF950044, 0 0 8px 3px #FF950033}
    100%{box-shadow:0 0 0 0px #FF950000, 0 0 0px 0px #FF950000}
  }
  @keyframes sfDiamondDrop{
    from{opacity:0;transform:translate(-50%,-40px) rotate(45deg) scale(0.5)}
    to{opacity:1;transform:translate(-50%,0) rotate(45deg) scale(1)}
  }
  @keyframes kpiSlideOut{from{opacity:1;transform:scale(1)}to{opacity:0;transform:scale(0.95)}}
  @keyframes kpiSlideIn{from{opacity:0;transform:scale(0.97)}to{opacity:1;transform:scale(1)}}
  @keyframes simCardGrow{from{opacity:0;max-height:0}to{opacity:1;max-height:200px}}
`}</style>

      {/* ── Row 1: 6 KPI cards (2 cols each) + Row 2: 12 month cards ── */}
      {(()=>{
        // Use pool-aware values — these shadow global names within this IIFE.
        // When the what-if sim is open, use the sim-adjusted stockout instead
        // of the base projection so the KPI cards reflect the sim scenario.
        const todayInventory = poolInventory;
        const stockoutInfo = simOpen && simAwareStockout !== undefined ? (simAwareStockout || poolStockout) : poolStockout;
        const invSummary = poolInvSummary;
        const MONTHS_FULL = ["January","February","March","April","May","June","July","August","September","October","November","December"];
        const kpiLabel = kpiFrom === "jan" ? "Full year" : "From today";

        const curKey = mkKey(currentYear, TODAY.getMonth()+1);
        const curTarget = resolveTargetForKey(curKey);
        const curActual = Math.round((months[curKey]?.actualSold || 0) * _selPct);
        const dim = getDaysInMonth(currentYear, TODAY.getMonth()+1);
        const dayOfMonth = TODAY.getDate();
        const pace = dayOfMonth > 0 ? Math.round(curActual / dayOfMonth * dim) : 0;
        const progressPct = curTarget > 0 ? Math.min(100, Math.round(curActual / curTarget * 100)) : 0;
        const statusColor = curTarget === 0 ? T.textSub : pace >= curTarget ? T.green : pace >= curTarget * 0.7 ? T.orange : T.red;
        const statusLabel = curTarget === 0 ? "No target" : pace >= curTarget ? "On Track" : pace >= curTarget * 0.7 ? "Behind" : "Off Track";
        // Year Total KPI — always calendar year (fixed reference point).
        // Uses resolveTargetForKey so per-marketplace overrides are included.
        const _yearResolvedTargets = Array.from({length:12},(_,i)=>resolveTargetForKey(mkKey(currentYear,i+1)));
        const yearTotalTarget = _yearResolvedTargets.reduce((a,b)=>a+b,0);
        const yearTotalActual = Math.round(Array.from({length:12},(_,i)=>(months[mkKey(currentYear,i+1)]?.actualSold||0)).reduce((a,b)=>a+b,0) * _selPct);
        const hitRate = yearTotalTarget > 0 ? Math.round(yearTotalActual / yearTotalTarget * 100) : 0;
        // Pace-aware color: compare actual progress vs expected progress through the year.
        // Expected = sum of resolved targets for completed months + prorated current month.
        const curMonthIdx = TODAY.getMonth(); // 0-based
        const curDayFrac = TODAY.getDate() / new Date(currentYear, curMonthIdx + 1, 0).getDate();
        const expectedSoFar = Math.round(
          _yearResolvedTargets.slice(0, curMonthIdx).reduce((s, t) => s + t, 0) + // completed months
          (_yearResolvedTargets[curMonthIdx] || 0) * curDayFrac // prorated current month
        );
        const paceRate = expectedSoFar > 0 ? yearTotalActual / expectedSoFar : (yearTotalActual > 0 ? 2 : 0);
        // Green = on track or ahead (≥80% of expected pace), orange = slightly behind (50-80%), red = way behind (<50%)
        const hitColor = paceRate >= 0.8 ? T.green : paceRate >= 0.5 ? T.orange : T.red;

        // Bumped from 100 → 116 to fit the new inventory line on the Year
        // Total card. All cards in the row use the same height for alignment.
        const KPI_H = 116;
        const cardStyle = (border) => ({
          gridColumn:"span 2", background:T.card, border:`1px solid ${border||T.border}`,
          borderRadius:RADIUS.lg, padding:"12px 14px", display:"flex", flexDirection:"column", minHeight:KPI_H, maxHeight:KPI_H, overflow:"hidden"
        });
        const titleRow = (title, tip, tipKey, tipVars) => (
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
            <span style={{ fontSize:12, fontWeight:700, color:T.textSub, letterSpacing:0.4, textTransform:"uppercase", lineHeight:1.2 }}>{title}</span>
            {tipKey
              ? <Info tip={tipKey} tipVars={tipVars}/>
              : tip && <Tooltip content={tip}><span style={{ width:16, height:16, borderRadius:"50%", border:`1.5px solid ${T.textSub}35`, display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:T.textSub, cursor:"help", flexShrink:0 }}>i</span></Tooltip>}
          </div>
        );
        return (
          <div style={{ display:"grid", gridTemplateColumns:isMobile?"repeat(2,1fr)":"repeat(12,1fr)", gap:8, marginBottom:10 }}>

            {/* When What If is ON: simulator replaces cards 1-5 */}
            {simOpen ? (
              <div style={{ gridColumn:"span 10", background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"12px 14px", minHeight:KPI_H, maxHeight: simMode === "custom" ? "none" : KPI_H, overflow:"visible", display:"flex", gap:14, animation:"kpiSlideIn 0.25s ease both" }}>
                {/* Left: controls */}
                <div style={{ flex:1, minWidth:0, display:"flex", flexDirection:"column" }}>
                  {/* Row 1: Title + tooltip + mode toggle */}
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                    <span style={{ fontSize:15, fontWeight:700, color:T.text }}>🧪 What If Simulator</span>
                    <Info tip="whatIfSimulator"/>
                    <div style={{ display:"flex", gap:0, borderRadius:RADIUS.sm, overflow:"hidden", border:`1px solid ${T.border}` }}>
                      {[["all","All months"],["custom","Per month"]].map(([m,label])=>(
                        <button key={m} onClick={()=>setSimMode(m)}
                          style={{ padding:"3px 12px", fontSize:12, fontFamily:FONT, cursor:"pointer", fontWeight:simMode===m?700:400, border:"none",
                            background:simMode===m?T.accent+"18":"transparent", color:simMode===m?T.accent:T.textSub }}>{label}</button>
                      ))}
                    </div>
                  </div>
                  {/* Row 2: Controls */}
                  {simMode === "all" ? (
                    <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                      {[-20,-10,0,10,20,30,50].map(p => (
                        <button key={p} onClick={()=>setSimPct(p)}
                          style={{ padding:"4px 12px", borderRadius:RADIUS.sm, fontSize:12, fontFamily:FONT, cursor:"pointer", fontWeight:simPct===p?700:400,
                            background:simPct===p?(p>=0?T.green:T.red)+"18":"transparent",
                            border:`1px solid ${simPct===p?(p>=0?T.green:T.red):T.border}`,
                            color:simPct===p?(p>=0?T.green:T.red):T.textSub }}>{p>=0?"+":""}{p}%</button>
                      ))}
                      <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                        <span style={{ fontSize:12, color:T.textSub }}>Custom:</span>
                        <input type="number" value={simPct||""} onChange={e=>setSimPct(parseInt(e.target.value)||0)}
                          style={{ width:60, background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.text, fontSize:13, fontWeight:700, padding:"4px 8px", outline:"none", fontFamily:FONT, textAlign:"center" }}/>
                        <span style={{ fontSize:12, color:T.textSub }}>%</span>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      {/* Per-month inline editor — each month has its own % input.
                          Honors the kpiFrom toggle: when "today" is active, the editor shows
                          the rolling 12-month window starting from the current month, NOT
                          calendar Jan-Dec. */}
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                        {viewMonths.map((vm, idx) => {
                          const i = vm.month;
                          const k = mkKey(vm.year, vm.month + 1);
                          const cPct = simCustom[k] || 0;
                          // Past = strictly before this current calendar month
                          const isPast = (vm.year < TODAY.getFullYear()) || (vm.year === TODAY.getFullYear() && vm.month < TODAY.getMonth());
                          const hasVal = cPct !== 0;
                          return (
                            <div key={k} style={{
                              display:"inline-flex", alignItems:"center", gap:4,
                              padding:"4px 6px 4px 10px",
                              borderRadius:RADIUS.sm,
                              background: hasVal ? (cPct>0?T.green:T.red)+"15" : "transparent",
                              border:`1px solid ${hasVal ? (cPct>0?T.green:T.red) : T.border}`,
                              opacity: isPast ? 0.4 : 1,
                            }}>
                              <span style={{ fontSize:12, fontWeight:hasVal?700:600, color: hasVal ? (cPct>0?T.green:T.red) : T.textSub, fontFamily:FONT, minWidth:26 }}>
                                {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i]}{vm.year !== currentYear ? <span style={{ fontSize:9, opacity:0.7 }}>'{String(vm.year).slice(-2)}</span> : null}
                              </span>
                              <input type="number"
                                disabled={isPast}
                                value={cPct === 0 ? "" : cPct}
                                placeholder="0"
                                onChange={e => {
                                  const v = e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0;
                                  setSimCustom(prev => ({ ...prev, [k]: v }));
                                }}
                                style={{
                                  width: 42,
                                  background: T.surface,
                                  border:`1px solid ${T.border}`,
                                  borderRadius: RADIUS.sm,
                                  color: hasVal ? (cPct>0?T.green:T.red) : T.text,
                                  fontSize:12, fontWeight:700,
                                  padding:"2px 4px",
                                  outline:"none",
                                  fontFamily:FONT,
                                  textAlign:"center",
                                }}
                              />
                              <span style={{ fontSize:11, color:T.textMuted }}>%</span>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginTop:2 }}>
                        <span style={{ fontSize:11, color:T.textMuted }}>Tip: edit any month independently. Use Clear to reset all.</span>
                        <span style={{ flex:1 }}/>
                        <button onClick={()=>setSimCustom({})} style={{
                          padding:"3px 10px", borderRadius:RADIUS.sm, fontSize:11, fontFamily:FONT, cursor:"pointer",
                          background:"transparent", border:`1px solid ${T.border}`, color:T.textSub,
                        }}>Clear all</button>
                      </div>
                    </div>
                  )}
                </div>
                {/* Right: 4 mini KPIs in 2×2 grid */}
                {(()=>{
                  // Sum the simulated total over the SAME 12-month window the user is viewing
                  // (rolling-from-today or full-calendar-year), so the sim respects kpiFrom.
                  let simTotal=0;
                  for (const vm of viewMonths) {
                    const k = mkKey(vm.year, vm.month + 1);
                    const t = resolveTargetForKey(k);
                    const pct = simMode === "all" ? simPct : (simCustom[k] || 0);
                    simTotal += Math.round(t * (1 + pct/100));
                  }
                  // Build arrival-by-day map for sim stockout (same data as chart)
                  const _simArrivals = {};
                  for (const arr of stockArrivals) {
                    if (arr.date <= TODAY) continue;
                    const _dk = `${arr.date.getFullYear()}-${arr.date.getMonth()+1}-${arr.date.getDate()}`;
                    _simArrivals[_dk] = (_simArrivals[_dk] || 0) + arr.units;
                  }
                  const simStockout = todayInventory > 0 ? (()=>{ let stk=todayInventory; for(let d=0;d<365;d++){const dt=new Date(TODAY.getTime()+d*86400000);const k=mkKey(dt.getFullYear(),dt.getMonth()+1);const t=resolveTargetForKey(k);const pct=simMode==="all"?simPct:(simCustom[k]||0);const simT=Math.round(t*(1+pct/100));const adk=`${dt.getFullYear()}-${dt.getMonth()+1}-${dt.getDate()}`;stk+=(_simArrivals[adk]||0);stk-=simT/getDaysInMonth(dt.getFullYear(),dt.getMonth()+1);if(stk<=0)return d;} return null; })() : null;
                  const simSoDays = simStockout!=null ? Math.max(0,simStockout-stockoutBuffer) : null;
                  const simReorder = simSoDays!=null ? Math.max(0,simSoDays-effectiveLeadTime) : null;
                  const extraUnits = simTotal - yearTotalTarget;
                  const costPerUnit = invSummary.weightedCostPerUnit || initialCostPerUnit || 0;
                  const simInvValue = (invSummary.totalValue||0) + (invSummary.inTransitValue||0) + (extraUnits > 0 ? extraUnits * costPerUnit : 0);
                  // Stockout color: red when 0 or below lead time, orange when within ~lead+buffer, green above.
                  // When there is no stock data at all (todayInventory === 0), show 0 in red.
                  const soNoData = todayInventory <= 0;
                  const soDisplay = soNoData ? 0 : (simSoDays != null ? simSoDays : null);
                  const soOrangeThresh = effectiveLeadTime + 14;
                  const soGreenThresh = effectiveLeadTime + 30;
                  const soColor = soNoData
                    ? T.red
                    : (soDisplay == null
                        ? T.green
                        : soDisplay <= effectiveLeadTime ? T.red
                          : soDisplay <= soOrangeThresh ? T.orange
                          : soDisplay <= soGreenThresh ? T.orange
                          : T.green);
                  const soText = soNoData ? "0d" : (soDisplay != null ? `${soDisplay}d` : "365+");
                  return (
                    <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                      <div style={{ background:T.bg, borderRadius:RADIUS.sm, padding:"6px 8px", textAlign:"center", display:"flex", flexDirection:"column", justifyContent:"center", minWidth:80 }}>
                        <div style={{ fontSize:12, color:T.textSub, fontWeight:600 }}>{currentYear} Total</div>
                        <div style={{ fontSize:16, fontWeight:800, color:T.text }}>{simTotal.toLocaleString()}</div>
                      </div>
                      <div style={{ background:T.bg, borderRadius:RADIUS.sm, padding:"6px 8px", textAlign:"center", display:"flex", flexDirection:"column", justifyContent:"center", minWidth:80 }}>
                        <div style={{ fontSize:12, color:T.textSub, fontWeight:600 }}>Stockout</div>
                        <div style={{ fontSize:16, fontWeight:800, color:soColor }}>{soText}</div>
                      </div>
                      <div style={{ background:T.bg, borderRadius:RADIUS.sm, padding:"6px 8px", textAlign:"center", display:"flex", flexDirection:"column", justifyContent:"center", minWidth:80 }}>
                        <div style={{ fontSize:12, color:T.textSub, fontWeight:600 }}>Reorder</div>
                        <div style={{ fontSize:16, fontWeight:800, color: soNoData ? T.red : (simReorder!=null&&simReorder<=0?T.red:simReorder!=null&&simReorder<=7?T.orange:T.green) }}>{soNoData ? "Now!" : (simReorder!=null?(simReorder>0?`${simReorder}d`:"Now!"):"OK")}</div>
                      </div>
                      <div style={{ background:T.bg, borderRadius:RADIUS.sm, padding:"6px 8px", textAlign:"center", display:"flex", flexDirection:"column", justifyContent:"center", minWidth:80 }}>
                        <div style={{ fontSize:12, color:T.textSub, fontWeight:600 }}>Blocked in Inv.</div>
                        <div style={{ fontSize:16, fontWeight:800, color:T.orange }}>{fmtFull(simInvValue)}</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
            <>

            {/* Card 1: Hero Month */}
            <div style={{ ...cardStyle(statusColor), borderLeft:`4px solid ${statusColor}`, animation:"kpiSlideIn 0.25s ease both" }}>
              {titleRow(MONTHS_FULL[TODAY.getMonth()], null, "heroMonth", {
                target: curTarget.toLocaleString(),
                actual: curActual.toLocaleString(),
                dayOfMonth: TODAY.getDate(),
                daysInMonth: getDaysInMonth(TODAY.getFullYear(), TODAY.getMonth()+1),
                pace: pace.toLocaleString(),
                weightedPayoutLine: weightedPayout > 0 ? `Expected cash this month: ${fmtFull(curTarget*weightedPayout)} at ${currSym()}${weightedPayout.toFixed(2)}/unit blended payout.` : "",
              })}
              <div style={{ flex:1, display:"flex", flexDirection:"column", justifyContent:"center" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:6 }}>
                  <div>
                    <span style={{ fontSize:22, fontWeight:800, color:T.text, letterSpacing:-0.5 }}>{curActual.toLocaleString()}</span>
                    <span style={{ fontSize:12, color:T.textSub, marginLeft:4 }}>/ {curTarget.toLocaleString()}</span>
                  </div>
                  <span style={{ fontSize:12, fontWeight:700, color:statusColor, padding:"2px 7px", borderRadius:RADIUS.pill, background:statusColor+"14", textTransform:"uppercase" }}>{statusLabel}</span>
                </div>
                <div style={{ height:6, borderRadius:3, background:T.surface, overflow:"hidden", marginBottom:4 }}>
                  <div style={{ height:"100%", borderRadius:3, background:statusColor, width:`${progressPct}%`, transition:"width 0.6s ease" }} />
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:T.textSub }}>
                  <span>Pace: ~{pace.toLocaleString()}</span>
                  <span>Day {dayOfMonth}/{dim}</span>
                </div>
              </div>
            </div>

            {/* Card 2: Year Total + Hit Rate + Current inventory.
                Inventory uses `poolInventory` which is computed via the
                stock-resolution hierarchy (pool > marketplace > group >
                product > API > zero) and respects the marketplace selector
                — when the user picks a single marketplace, only that
                marketplace's pool contributes. */}
            <div style={cardStyle(paceRate < 0.5 ? T.red : paceRate < 0.8 ? T.orange : undefined)}>
              {titleRow(`${currentYear} Total`, null, "yearTotalKpi", {
                yearTarget: yearTotalTarget.toLocaleString(),
                yearActual: yearTotalActual.toLocaleString(),
                hitRate: hitRate,
                expected: Math.round(expectedSoFar).toLocaleString(),
                paceRate: Math.round(paceRate * 100),
                inventory: poolInventory.toLocaleString(),
              })}
              <div style={{ flex:1, display:"flex", flexDirection:"column", justifyContent:"center" }}>
                <div style={{ fontSize:22, fontWeight:800, color:T.text, letterSpacing:-0.5, textAlign:"center", lineHeight:1 }}>{yearTotalTarget.toLocaleString()}</div>
                <div style={{ height:5, borderRadius:3, background:T.surface, overflow:"hidden", margin:"6px 0 3px" }}>
                  <div style={{ height:"100%", borderRadius:3, background:hitColor, width:`${Math.min(100,hitRate)}%`, transition:"width 0.6s ease" }} />
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:T.textSub }}>
                  <span style={{ color:hitColor, fontWeight:700 }}>{hitRate}% hit</span>
                  <span>{yearTotalActual.toLocaleString()} sold</span>
                </div>
                {/* Current inventory line — uses the resolved hierarchy stock
                    and respects the marketplace selector via poolInventory. */}
                <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:4, marginTop:3, fontSize:11, color:poolInventory > 0 ? T.orange : T.textSub }}>
                  <span>📦</span>
                  <span style={{ fontWeight:700 }}>{poolInventory > 0 ? `${poolInventory.toLocaleString()} in stock` : "no stock data"}</span>
                  {poolInventory === 0 && <Tooltip content="No stock data — set initial stock on a pool, connect a marketplace API, or load sample data on a marketplace card."><span style={{ fontSize:11, cursor:"help", animation:"sfPulse 2s ease infinite", textShadow:`0 0 6px ${T.orange}` }}>⚠️</span></Tooltip>}
                </div>
              </div>
            </div>

            {/* Card 3: Stockout */}
            {(()=>{
              const rawDays = stockoutInfo ? stockoutInfo.daysAway : null;
              const adjusted = rawDays != null ? Math.max(0, rawDays - stockoutBuffer) : null;
              const soGreen = months?._appSettings?.stockoutThresholds?.stockoutGreen ?? 60;
              const soOrange = months?._appSettings?.stockoutThresholds?.stockoutOrange ?? 30;
              const soColor = adjusted==null?T.green:adjusted>soGreen?T.green:adjusted>soOrange?T.orange:T.red;
              return (
              <div style={cardStyle(adjusted!=null&&adjusted<=30?T.red:undefined)}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:T.textSub, letterSpacing:0.4, textTransform:"uppercase", lineHeight:1.2 }}>Stockout</span>
                  <div style={{ display:"flex", gap:3, alignItems:"center" }}>
                    {todayInventory===0 && <Tooltip content="No stock data — add initial stock or orders with arrived units."><span style={{ fontSize:14, cursor:"help", animation:"sfPulse 2s ease infinite", textShadow:`0 0 8px ${T.red}` }}>⚠️</span></Tooltip>}
                    <Info tip="stockoutKpi" tipVars={{
                      todayInventory: todayInventory.toLocaleString(),
                      monthlyTarget: (months[mkKey(currentYear, TODAY.getMonth()+1)]?.target || 0).toLocaleString(),
                      rawDays: rawDays != null ? rawDays : "365+",
                      buffer: stockoutBuffer,
                      adjusted: adjusted != null ? adjusted : "365+",
                    }}/>
                  </div>
                </div>
                <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                  <div style={{ fontSize:28, fontWeight:800, color:soColor, letterSpacing:-1, lineHeight:1 }}>
                    {adjusted!=null?`${adjusted}`:"365+"} <span style={{ fontSize:14, fontWeight:600 }}>days</span>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:4 }}>
                    <Tooltip content="Safety buffer — days subtracted from the raw stockout projection. Set to e.g. 14 to get a 2-week early warning. ⚡ Affects: the Stockout KPI number on this page and the red stockout line on the chart. A higher buffer means earlier warning.">
                    <span style={{ fontSize:12, color:T.textSub }}>Buffer</span>
                    </Tooltip>
                    <input type="number" value={stockoutBuffer||""} onChange={e=>setStockoutBuffer(Math.max(0,parseInt(e.target.value)||0))}
                      style={{ width:32, background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.text, padding:"1px 3px", fontSize:12, fontWeight:700, outline:"none", fontFamily:FONT, textAlign:"center" }}/>
                    <span style={{ fontSize:12, color:T.textSub }}>days</span>
                  </div>
                </div>
              </div>
              );
            })()}

            {/* Card 4: Reorder */}
            {(()=>{
              const leadSrc = customLeadTime > 0 ? "custom" : leadTimeInfo.count > 0 ? "last order" : "estimate";
              const reorderBorder = reorderAlert?.needsReorder ? (reorderAlert.daysUntilOrderBy<=7?T.red:reorderAlert.daysUntilOrderBy<=30?T.orange:undefined) : undefined;
              return (
              <div style={cardStyle(reorderBorder)}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:T.textSub, letterSpacing:0.4, textTransform:"uppercase", lineHeight:1.2 }}>Reorder</span>
                  <div style={{ display:"flex", gap:3, alignItems:"center" }}>
                    {orders.length===0 && <Tooltip content="No orders added yet — add an order to calculate reorder timing."><span style={{ fontSize:14, cursor:"help", animation:"sfPulse 2s ease infinite", textShadow:`0 0 8px ${T.red}` }}>⚠️</span></Tooltip>}
                    <Info tip="reorderKpi" tipVars={{
                      stockoutDays: reorderAlert?.daysAway != null ? reorderAlert.daysAway : "365+",
                      leadTime: effectiveLeadTime,
                      reorderDays: reorderAlert?.daysUntilOrderBy != null ? reorderAlert.daysUntilOrderBy : "—",
                      orderCount: leadTimeInfo.count,
                    }}/>
                  </div>
                </div>
                <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                  <div style={{ fontSize:28, fontWeight:800, color:reorderAlert.daysUntilOrderBy<=7?T.red:reorderAlert.daysUntilOrderBy<=30?T.orange:T.green, letterSpacing:-1, lineHeight:1 }}>
                    {reorderAlert.daysUntilOrderBy>0?<>{reorderAlert.daysUntilOrderBy}<span style={{ fontSize:14, fontWeight:600 }}> days</span></>:"Now!"}
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:4 }}>
                    <Tooltip content={`Lead time in days — how long from placing an order to receiving stock. ${customLeadTime>0?"Custom override active — calculation based on your input.":"Leave at 0 to auto-calculate from your last order ("+leadTimeInfo.lastOrder+" days)."} ⚡ Affects: the Reorder KPI deadline (stockout date minus lead time = when to order). A longer lead time means you need to order earlier.`}>
                    <span style={{ fontSize:12, color:customLeadTime>0?T.accent:T.textSub }}>Lead time</span>
                    </Tooltip>
                    <input type="number" value={customLeadTime||""} onChange={e=>setCustomLeadTime(Math.max(0,parseInt(e.target.value)||0))}
                      placeholder={String(leadTimeInfo.lastOrder)}
                      style={{ width:32, background:T.surface, border:`1px solid ${customLeadTime>0?T.accent:T.border}`, borderRadius:RADIUS.sm, color:customLeadTime>0?T.accent:T.text, padding:"1px 3px", fontSize:12, fontWeight:700, outline:"none", fontFamily:FONT, textAlign:"center" }}/>
                    <span style={{ fontSize:12, color:T.textSub }}>days</span>
                  </div>
                </div>
              </div>
              );
            })()}

            {/* Card 5: Stock Alert */}
            {(()=>{
              const hasArrivedStock = initialStock > 0 || orders.some(o=>(o.containerBatches||[]).some(b=>{ const d=parseDate(b.arrivalDate); return d&&d<=TODAY; }));
              const hasOrderDates = orders.some(o=>(o.containerBatches||[]).some(b=>b.arrivalDate||b.inStockDate));
              const warnText = !hasArrivedStock && !hasOrderDates
                ? "No stock data — add initial stock or orders with arrived units to enable stock alerts."
                : (!hasOrderDates && hasArrivedStock && todayInventory > 0
                    ? "No orders added yet — add a supplier order so stock alerts can compute reorder timing."
                    : null);
              return (
              <div style={cardStyle(showDanger?T.red:undefined)}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
                  <span style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:12, fontWeight:700, color:T.textSub, letterSpacing:0.4, textTransform:"uppercase", lineHeight:1.2 }}>
                    Stock Alert
                    <Info tip="stockAlertKpi"/>
                    {warnText && <Tooltip content={warnText}><span style={{ fontSize:14, cursor:"help", animation:"sfPulse 2s ease infinite", textShadow:`0 0 6px ${T.orange}`, lineHeight:1 }}>⚠️</span></Tooltip>}
                  </span>
                  <button onClick={()=>saveDangerOn(!showDanger)} style={{ background:showDanger?T.red+"18":"transparent", border:`1px solid ${showDanger?T.red:T.border}`, borderRadius:RADIUS.sm, padding:"1px 7px", fontSize:12, fontFamily:FONT, cursor:"pointer", color:showDanger?T.red:T.textSub, fontWeight:600 }}>
                    {showDanger?"ON":"OFF"}
                  </button>
                </div>
                <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                  {showDanger ? (
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:3 }}>
                        <span style={{ fontSize:12, color:T.textSub, minWidth:70 }}>Threshold</span>
                        <input type="text" inputMode="numeric" value={dangerPctStr}
                          onChange={e=>setDangerPctStr(e.target.value)}
                          onBlur={e=>{const v=parseFloat(e.target.value);const c=isNaN(v)?10:Math.min(99,Math.max(1,v));saveDangerPct(c);setDangerPctStr(String(c));}}
                          onKeyDown={e=>{if(e.key==="Enter")e.target.blur();}}
                          style={{ width:32, background:T.surface, border:`1px solid ${T.red}`, borderRadius:RADIUS.sm, color:T.red, padding:"2px 4px", fontSize:12, fontWeight:700, outline:"none", fontFamily:FONT, textAlign:"center" }}/>
                        <span style={{ fontSize:12, color:T.textSub }}>%</span>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:3 }}>
                        <span style={{ fontSize:12, color:T.orange, minWidth:70 }}>Shrinkage</span>
                        <input type="text" inputMode="numeric" value={shrinkagePctStr}
                          onChange={e=>setShrinkagePctStr(e.target.value)}
                          onBlur={e=>{const v=parseFloat(e.target.value);const c=isNaN(v)?2:Math.min(20,Math.max(0,v));saveShrinkagePct(c);setShrinkagePctStr(String(c));}}
                          onKeyDown={e=>{if(e.key==="Enter")e.target.blur();}}
                          style={{ width:32, background:T.surface, border:`1px solid ${T.orange}`, borderRadius:RADIUS.sm, color:T.orange, padding:"2px 4px", fontSize:12, fontWeight:700, outline:"none", fontFamily:FONT, textAlign:"center" }}/>
                        <span style={{ fontSize:12, color:T.textSub }}>%</span>
                      </div>
                    </div>
                  ) : <div style={{ fontSize:14, color:T.textSub }}>OFF</div>}
                </div>
              </div>
              );
            })()}

            </>
            )}

            {/* Card 6: What If — always visible as toggle. When sim is open in per-month
                mode, allow it to grow taller to match the sim panel beside it. */}
            <div ref={whatIfRef} onClick={()=>setSimOpen(!simOpen)} style={{ ...cardStyle(simOpen?T.orange:undefined), maxHeight: simOpen && simMode === "custom" ? "none" : KPI_H, cursor:"pointer", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden" }}>
              <style>{`
                @keyframes chBubA{0%{opacity:0;transform:translateY(0) scale(0)}15%{opacity:0.9;transform:translateY(-6px) scale(1)}85%{opacity:0.4;transform:translateY(-32px) scale(0.7)}100%{opacity:0;transform:translateY(-42px) scale(0)}}
                @keyframes chBubB{0%{opacity:0;transform:translateY(0) scale(0)}20%{opacity:0.85;transform:translateY(-8px) scale(0.9)}80%{opacity:0.3;transform:translateY(-30px) scale(0.5)}100%{opacity:0;transform:translateY(-40px) scale(0)}}
                @keyframes chBubC{0%{opacity:0;transform:translateY(0) scale(0)}25%{opacity:1;transform:translateY(-4px) scale(1.1)}75%{opacity:0.5;transform:translateY(-26px) scale(0.6)}100%{opacity:0;transform:translateY(-38px) scale(0)}}
                @keyframes chTwinkle{0%,100%{opacity:0;transform:scale(0) rotate(0deg)}50%{opacity:1;transform:scale(1) rotate(180deg)}}
              `}</style>
              <div style={{ position:"absolute", inset:0, pointerEvents:"none", overflow:"hidden" }}>
                <span style={{ position:"absolute", left:"8%",  bottom:"15%", fontSize:6, color:T.orange,  animation:"chBubA 3.2s ease 0s infinite" }}>●</span>
                <span style={{ position:"absolute", left:"22%", bottom:"5%",  fontSize:5, color:"#FFD60A",animation:"chBubB 3.6s ease 0.4s infinite" }}>○</span>
                <span style={{ position:"absolute", left:"38%", bottom:"20%", fontSize:7, color:T.accent, animation:"chBubC 2.8s ease 0.8s infinite" }}>●</span>
                <span style={{ position:"absolute", left:"55%", bottom:"8%",  fontSize:5, color:T.orange,  animation:"chBubA 3.4s ease 1.2s infinite" }}>○</span>
                <span style={{ position:"absolute", left:"70%", bottom:"18%", fontSize:6, color:"#FFD60A",animation:"chBubB 3s ease 1.6s infinite" }}>●</span>
                <span style={{ position:"absolute", left:"85%", bottom:"12%", fontSize:5, color:T.accent, animation:"chBubC 3.8s ease 2s infinite" }}>○</span>
                <span style={{ position:"absolute", left:"15%", top:"20%",   fontSize:7, color:T.orange,  animation:"chTwinkle 2.4s ease 0.2s infinite" }}>✦</span>
                <span style={{ position:"absolute", left:"45%", top:"12%",   fontSize:6, color:"#FFD60A",animation:"chTwinkle 2.8s ease 0.6s infinite" }}>✧</span>
                <span style={{ position:"absolute", left:"75%", top:"22%",   fontSize:7, color:T.accent, animation:"chTwinkle 2.6s ease 1.0s infinite" }}>✦</span>
                <span style={{ position:"absolute", left:"30%", top:"45%",   fontSize:5, color:T.orange,  animation:"chTwinkle 3.2s ease 1.4s infinite" }}>✧</span>
                <span style={{ position:"absolute", left:"62%", top:"50%",   fontSize:6, color:T.accent, animation:"chTwinkle 2.2s ease 1.8s infinite" }}>✦</span>
              </div>
              <span style={{ fontSize:28, position:"relative", zIndex:1 }}>🧪</span>
              <span style={{ fontSize:12, fontWeight:700, color:simOpen?T.orange:T.textSub, letterSpacing:0.4, textTransform:"uppercase", marginTop:4, position:"relative", zIndex:1 }}>{simOpen?"Close":"What if?"}</span>
            </div>

            {/* Info row — title + pills + year stats + exports */}
            <div style={{ gridColumn:"1/-1", display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", padding:"4px 0" }}>
              <div style={{ display:"flex", alignItems:"center", gap:3 }}>
                <span style={{ fontSize:14, fontWeight:700, color:T.text, letterSpacing:-0.3 }}>🎯 Monthly Targets</span>
                <Info tip="target"/>
              </div>
              <Tooltip content={kpiFrom==="jan"?"Showing full calendar year (Jan–Dec). Click to switch to a rolling 12-month view starting from the current month.":"Showing 12 months from today forward (may span into next year). Click to switch to the full calendar year view."}>
              <button onClick={()=>setKpiFrom(kpiFrom==="jan"?"today":"jan")}
                style={{ padding:"2px 8px", borderRadius:RADIUS.pill, fontSize:12, fontFamily:FONT, cursor:"pointer", fontWeight:700, background:kpiFrom==="today"?T.accent+"18":"transparent", border:`1px solid ${kpiFrom==="today"?T.accent:T.border}`, color:kpiFrom==="today"?T.accent:T.textSub }}>
                {kpiLabel}
              </button>
              </Tooltip>
              {marketplaces.length>0 && <div style={{ width:1, height:12, background:T.border, flexShrink:0 }}/>}
              {marketplaces.length>0 && (
                <MktSelector
                  marketplaces={marketplaces}
                  inventoryPools={inventoryPools}
                  selMkts={selMkts}
                  setSelMkts={setSelMkts}
                  compact
                />
              )}
              {/* ── Product filter (visible only when single marketplace selected) ── */}
              {isSingleMkt && selectedMkt && (selectedMkt.products||[]).length > 0 && (
                <>
                  <div style={{ width:1, height:12, background:T.border, flexShrink:0 }}/>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ fontSize:11, color:T.textSub, fontWeight:600 }}>Product:</span>
                    <StyledSelect sm value={selProductId || ""} onChange={e => setSelProductId(e.target.value || null)} style={{ minWidth:130 }}>
                      <option value="">All products</option>
                      {(selectedMkt.products||[])
                        .slice()
                        .sort((a,b) => (b.pctOfMarket||0) - (a.pctOfMarket||0))
                        .map(p => (
                          <option key={p.id} value={p.id}>
                            {p.name || p.id} ({Math.round((p.pctOfMarket||0)*100)}%)
                          </option>
                        ))}
                    </StyledSelect>
                    {selProductId && (() => {
                      // Check if this product has any manual overrides
                      const hasOverride = viewMonths.some(vm => {
                        const k = mkKey(vm.year, vm.month+1);
                        return months[k]?.[productPctKey(selectedMkt.id, selProductId)] != null;
                      });
                      if (!hasOverride) return null;
                      return (
                        <Tooltip content="Reset this product's targets to its sales-share percentage for all visible months">
                          <button onClick={() => {
                            const prod = selectedMkt.products.find(p => p.id === selProductId);
                            if (!prod) return;
                            for (const vm of viewMonths) {
                              const k = mkKey(vm.year, vm.month+1);
                              const key = productPctKey(selectedMkt.id, selProductId);
                              if (months[k]?.[key] != null) {
                                monthOps.updateMonth(k, { [key]: null });
                              }
                            }
                          }}
                          style={{
                            padding:"4px 10px", borderRadius:RADIUS.pill, fontSize:10,
                            fontFamily:FONT, fontWeight:700, cursor:"pointer",
                            background:T.orange+"15", border:`1px solid ${T.orange}40`, color:T.orange,
                          }}>↻ Reset</button>
                        </Tooltip>
                      );
                    })()}
                  </div>
                </>
              )}
              <div style={{ width:1, height:12, background:T.border, flexShrink:0 }}/>
              {(()=>{
                const mt = viewMonths.map(vm=>{
                  const k=mkKey(vm.year,vm.month+1); const raw=months[k]?.target||0;
                  if(isSingleMkt&&selectedMkt){ const ov=months[k]?.[`mktTarget_${selectedMkt.id}`]; return ov!=null?ov:Math.round(raw*(selectedMkt.pctOfTotal||0)); }
                  if(isPoolSelection) return Math.round(raw*poolPct);
                  return raw;
                });
                const ma = viewMonths.map(vm=>Math.round((months[mkKey(vm.year,vm.month+1)]?.actualSold||0)*_selPct));
                const yt=mt.reduce((a,b)=>a+b,0), ya=ma.reduce((a,b)=>a+b,0), avg=Math.round(yt/12);
                const best=mt.indexOf(Math.max(...mt)), hr=yt>0?Math.round(ya/yt*100):0;
                const rangeLabel = kpiFrom==="jan" ? `${currentYear}` : `${MONTH_NAMES[viewMonths[0].month].slice(0,3)} ${viewMonths[0].year}–${MONTH_NAMES[viewMonths[11].month].slice(0,3)} ${viewMonths[11].year}`;
                return <>
                  <span style={{ fontSize:12, fontWeight:700, color:T.accent }}>{rangeLabel}</span>
                  <span style={{ fontSize:12, color:T.textSub }}><strong style={{ color:T.text }}>{yt.toLocaleString()}</strong> target</span>
                  {ya>0&&<span style={{ fontSize:12, color:T.textSub }}><strong style={{ color:T.green }}>{ya.toLocaleString()}</strong> sold</span>}
                  <span style={{ fontSize:12, color:T.textSub }}><strong style={{ color:T.text }}>{avg}</strong> avg/mo</span>
                  {mt.some(t=>t>0)&&<span style={{ fontSize:12, color:T.textSub }}>Best: <strong style={{ color:T.green }}>{MONTH_NAMES[viewMonths[best].month].slice(0,3)} ({mt[best]})</strong></span>}
                  {ya>0&&yt>0&&<span style={{ fontSize:12, color:T.textSub }}>Hit rate: <strong style={{ color:hr>=80?T.green:hr>=50?T.orange:T.red }}>{hr}%</strong></span>}
                </>;
              })()}
              <div style={{ flex:1 }}/>
              {(!can || can("export","view")) && <>
                <Tooltip content={isSingleMkt?`Download CSV — ${selectedMkt?.name} targets`:"Download CSV — all targets"}><button onClick={()=>{
                  const rows=[["Year","Month","Target","Actual Sold","Note"]];
                  visibleYears.slice().sort().forEach(y=>{for(let m=1;m<=12;m++){const k=mkKey(y,m);
                    let t=months[k]?.target||0;
                    if(isSingleMkt&&selectedMkt){const ov=months[k]?.[`mktTarget_${selectedMkt.id}`]; t=ov!=null?ov:Math.round(t*(selectedMkt.pctOfTotal||0));}
                    else if(isPoolSelection) t=Math.round(t*poolPct);
                    rows.push([y,MONTH_NAMES[m-1],t,months[k]?.actualSold||0,months[k]?.targetNote||""]);}});
                  const csv=rows.map(r=>r.join(",")).join("\n");
                  const a=document.createElement("a");a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));a.download=`targets-${new Date().toISOString().slice(0,10)}.csv`;a.click();
                }} style={{ display:"flex",alignItems:"center",gap:3,padding:"2px 7px",borderRadius:RADIUS.md,background:T.surface,border:`1px solid ${T.border}`,color:T.textSub,fontSize:12,fontFamily:FONT,cursor:"pointer",flexShrink:0 }}>📤 CSV</button></Tooltip>
                <Tooltip content={isSingleMkt?`Export PDF — ${selectedMkt?.name}`:"Export PDF — all targets"}><button onClick={()=>{
                  const chartEl=document.querySelector(".recharts-wrapper svg");let chartSVG="";
                  if(chartEl){const cl=chartEl.cloneNode(true);cl.setAttribute("width","780");cl.setAttribute("height","200");cl.style.background="#fff";cl.querySelectorAll("text").forEach(t=>{t.setAttribute("fill","#444");});cl.querySelectorAll(".recharts-cartesian-grid line").forEach(l=>{l.setAttribute("stroke","#e5e5e5");});chartSVG=cl.outerHTML;}
                  const sy=visibleYears.slice().sort();let yth="";
                  for(const year of sy){const mT=Array.from({length:12},(_,i)=>{const k=mkKey(year,i+1);const raw=months[k]?.target||0;if(isSingleMkt&&selectedMkt){const ov=months[k]?.[`mktTarget_${selectedMkt.id}`];return ov!=null?ov:Math.round(raw*(selectedMkt.pctOfTotal||0));}if(isPoolSelection)return Math.round(raw*poolPct);return raw;});const mA=Array.from({length:12},(_,i)=>Math.round((months[mkKey(year,i+1)]?.actualSold||0)*_selPct));const tT=mT.reduce((a,b)=>a+b,0);const tA=mA.reduce((a,b)=>a+b,0);const hR=tT>0?Math.round(tA/tT*100):0;const bI=mT.indexOf(Math.max(...mT));let rH="";for(let i=0;i<12;i++){const t=mT[i],a=mA[i],d=a>0?a-t:0;const dC=d>=0?"#34c759":"#ff3b30";const iN=year===new Date().getFullYear()&&i===new Date().getMonth();rH+=`<tr style="${iN?"background:#f0f7ff;":""}"><td style="padding:7px 12px;border-bottom:1px solid #eee;font-weight:${iN?700:400}">${MONTH_NAMES[i]}</td><td style="padding:7px 12px;border-bottom:1px solid #eee;text-align:right;font-weight:700">${t.toLocaleString()}</td><td style="padding:7px 12px;border-bottom:1px solid #eee;text-align:right;color:${a>0?"#34c759":"#aaa"};font-weight:600">${a>0?a.toLocaleString():"—"}</td><td style="padding:7px 12px;border-bottom:1px solid #eee;text-align:right;color:${dC};font-weight:600">${a>0?(d>=0?"+":"")+d.toLocaleString():"—"}</td></tr>`;}yth+=`<div style="break-inside:avoid;margin-bottom:28px"><h3 style="font-size:18px;font-weight:800;margin:0 0 4px;color:#111">${year} ${year===new Date().getFullYear()?"(Current)":""}</h3><div style="display:flex;gap:20px;margin-bottom:12px;font-size:12px;color:#666"><span><strong style="color:#111">${tT.toLocaleString()}</strong> target</span><span><strong style="color:#34c759">${tA.toLocaleString()}</strong> sold</span><span><strong style="color:#111">${Math.round(tT/12).toLocaleString()}</strong> avg/mo</span><span>Best: <strong style="color:#8b5cf6">${MONTH_NAMES[bI]} (${mT[bI].toLocaleString()})</strong></span><span>Hit rate: <strong style="color:${hR>=80?"#34c759":hR>=50?"#ff9500":"#ff3b30"}">${hR}%</strong></span></div><table style="width:100%;border-collapse:collapse;font-size:13px"><thead><tr style="border-bottom:2px solid #ddd"><th style="padding:8px 12px;text-align:left;font-size:11px;text-transform:uppercase;color:#888">Month</th><th style="padding:8px 12px;text-align:right;font-size:11px;text-transform:uppercase;color:#888">Target</th><th style="padding:8px 12px;text-align:right;font-size:11px;text-transform:uppercase;color:#888">Actual</th><th style="padding:8px 12px;text-align:right;font-size:11px;text-transform:uppercase;color:#888">Delta</th></tr></thead><tbody>${rH}<tr style="border-top:2px solid #333"><td style="padding:8px 12px;font-weight:700">Total</td><td style="padding:8px 12px;text-align:right;font-weight:800">${tT.toLocaleString()}</td><td style="padding:8px 12px;text-align:right;font-weight:800;color:#34c759">${tA.toLocaleString()}</td><td style="padding:8px 12px;text-align:right;font-weight:800;color:${tA-tT>=0?"#34c759":"#ff3b30"}">${tA>0?(tA-tT>=0?"+":"")+(tA-tT).toLocaleString():"—"}</td></tr></tbody></table></div>`;}
                  const w=window.open("","_blank");if(!w)return;
                  w.document.write(`<!DOCTYPE html><html><head><title>CashHorizon — Targets Report</title><style>body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;padding:28px 32px;color:#111;max-width:820px;margin:0 auto}h1{font-size:24px;font-weight:800;margin:0 0 4px;letter-spacing:-0.5px}.meta{font-size:12px;color:#888;margin:0 0 24px}.chart-wrap{margin-bottom:28px;padding:12px 0;border:1px solid #eee;border-radius:8px;overflow:hidden;text-align:center}.chart-wrap svg{max-width:100%}.years{column-count:${sy.length>1?2:1};column-gap:28px}@media print{@page{margin:14mm;size:A4}body{padding:0}.no-print{display:none}}tr:hover td{background:#fafafa}</style></head><body><h1>🎯 Monthly Targets</h1><div class="meta">CashHorizon · Generated ${new Date().toLocaleDateString()} · ${sy.join(", ")}</div>${chartSVG?`<div class="chart-wrap"><div style="font-size:11px;font-weight:600;color:#888;margin-bottom:8px;text-align:left;padding:0 12px">TARGET vs ACTUAL — ${chartYear}</div>${chartSVG}</div>`:""}<div class="years">${yth}</div><script>window.onload=()=>window.print();<\/script></body></html>`);
                  w.document.close();
                }} style={{ display:"flex",alignItems:"center",gap:3,padding:"2px 7px",borderRadius:RADIUS.md,background:T.surface,border:`1px solid ${T.border}`,color:T.textSub,fontSize:12,fontFamily:FONT,cursor:"pointer",flexShrink:0 }}>📄 PDF</button></Tooltip>
              </>}
            </div>

            {/* Marketplace / Pool banner — inside grid, above month cards */}
            {isSingleMkt&&selectedMkt&&(
              <div style={{ gridColumn:"1/-1", background:selectedMktColor+"10",border:`1px solid ${selectedMktColor+"28"}`,borderRadius:RADIUS.md,padding:"6px 12px",fontSize:10,color:T.textSub,lineHeight:1.4 }}>
                <strong style={{ color:selectedMktColor }}>📊 {selectedMkt.name}</strong> — {Math.round((selectedMkt.pctOfTotal||0)*100)}% of total. Edit any month individually. Orange badge = manual override.
              </div>
            )}
            {isPoolSelection && selectedPool && (
              <div style={{ gridColumn:"1/-1", background:T.accent+"10",border:`1px solid ${T.accent+"28"}`,borderRadius:RADIUS.md,padding:"6px 12px",fontSize:10,color:T.textSub,lineHeight:1.4 }}>
                <strong style={{ color:T.accent }}>🔗 {selectedPool.name}</strong> — {Math.round(poolPct*100)}% of brand ({(selectedPool.marketplaceIds||[]).map(id => marketplaces.find(m=>m.id===id)?.name).filter(Boolean).join(" + ")}). Shows the pool's combined share of brand-wide targets. Select an individual marketplace to edit its specific targets.
              </div>
            )}

            {/* Row 2: 12 month cards from viewMonths */}
            {(()=>{
              // Compute cumulative balance for each month (revenue from targets)
              let cumBal = 0, cumSimBal = 0;
              const balances = viewMonths.map((vm) => {
                const k = mkKey(vm.year, vm.month+1);
                const raw = months[k]?.target || 0;
                const mktPct = isSingleMkt && selectedMkt ? (selectedMkt.pctOfTotal||0) : 1;
                const target = isSingleMkt ? (months[k]?.[`mktTarget_${selectedMkt?.id}`] ?? Math.round(raw * mktPct)) : raw;
                const pct = simOpen ? (simMode==="all" ? simPct : (simCustom[k]||0)) : 0;
                const simTarget = Math.round(target * (1 + pct/100));
                const rev = target * (weightedPayout||0);
                const simRev = simTarget * (weightedPayout||0);
                cumBal += rev;
                cumSimBal += simRev;
                return { balance: cumBal, simBalance: cumSimBal };
              });
              return viewMonths.map((vm,i)=>(
                <div key={`hero_wrap_${vm.year}_${vm.month}`} style={{ gridColumn:"span 2", display:"flex", flexDirection:"column" }}>
                  <MonthCard key={`hero_${vm.year}_${vm.month}`}
                    year={vm.year} monthIdx={vm.month}
                    months={months} marketplaces={marketplaces} orders={orders}
                    monthOps={monthOps} saveStatus={saveStatus}
                    selMkts={selMkts} showDanger={showDanger} dangerPct={dangerPct}
                    isPrev={vm.year<currentYear||(vm.year===currentYear&&vm.month<TODAY.getMonth())} currentYear={currentYear}
                    lastEditedKey={lastEditedKey} setLastEditedKey={setLastEditedKey}
                    animKey={animKey} animDelay={0} can={can}
                    modifiers={modifiers} initialStock={effectiveStartingStock}
                    weightedPayout={weightedPayout} costPerUnit={initialCostPerUnit||0} shrinkagePct={shrinkagePct}
                    simPct={simOpen?(simMode==="all"?simPct:(simCustom[mkKey(vm.year,vm.month+1)]||0)):0}
                    monthBalance={balances[i].balance}
                    simMonthBalance={balances[i].simBalance}
                    isPoolSelection={isPoolSelection} poolPct={poolPct} selectedPoolName={selectedPool?.name||""}
                    onEditNote={(k,txt)=>{ setNoteTarget(k); setNoteText(txt||""); }}/>
                  {/* ── Per-product target row (visible when product filter is active) ── */}
                  {selProductId && isSingleMkt && selectedMkt && (() => {
                    const k = mkKey(vm.year, vm.month+1);
                    const mktTargetKey = `mktTarget_${selectedMkt.id}`;
                    const mktOv = months[k]?.[mktTargetKey] ?? null;
                    const rawTarget = months[k]?.target || 0;
                    const mktTarget = mktOv != null ? mktOv : Math.round(rawTarget * (selectedMkt.pctOfTotal || 0));
                    const alloc = allocateProductTargets(mktTarget, selectedMkt.products, selectedMkt.id, months[k] || {});
                    const prodAlloc = alloc.find(a => a.productId === selProductId);
                    if (!prodAlloc) return null;
                    const pctKeyStr = productPctKey(selectedMkt.id, selProductId);
                    return (
                      <div style={{
                        padding: "8px 12px", marginTop: 4, borderRadius: RADIUS.sm,
                        background: selectedMktColor + "0a",
                        border: `1px solid ${selectedMktColor}18`,
                        fontSize: 12, fontFamily: FONT,
                        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                      }}>
                        <span style={{ color: T.textSub, fontWeight: 600 }}>
                          {prodAlloc.name}:
                        </span>
                        <input
                          type="number"
                          value={prodAlloc.target}
                          onChange={e => {
                            const newUnits = parseInt(e.target.value) || 0;
                            const newPct = mktTarget > 0 ? Math.round((newUnits / mktTarget) * 10000) / 100 : 0;
                            monthOps.updateMonth(k, { [pctKeyStr]: newPct });
                          }}
                          style={{
                            background: T.surface, border: `1px solid ${selectedMktColor}44`,
                            borderRadius: RADIUS.sm, color: prodAlloc.isLocked ? T.accent : T.text,
                            fontSize: 13, fontWeight: 700, padding: "3px 6px", width: 60,
                            outline: "none", fontFamily: FONT, textAlign: "center",
                          }}
                        />
                        <span style={{ fontSize: 11, color: T.textSub }}>
                          units ({Math.round(prodAlloc.pct * 100)}%)
                        </span>
                        {prodAlloc.isLocked && (
                          <span style={{
                            fontSize: 9, padding: "2px 6px", borderRadius: RADIUS.pill,
                            background: T.orange + "18", color: T.orange, fontWeight: 700,
                          }}>locked %</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ));
            })()}
          </div>
        );
      })()}

      {/* Target Simulator Panel — now inline in KPI row, old floating panel removed */}

      {/* Launch Product Risk Panel */}
      {launchOpen && orders.length > 0 && (()=>{
        const futureOrders = orders.filter(o => {
          if (o.completed) return false;
          for (const cb of (o.containerBatches||[])) {
            const ad = parseDate(cb.arrivalDate);
            if (ad && ad > TODAY) return true;
          }
          return !o.containerBatches?.length; // no batches = still in planning
        });
        if (!futureOrders.length) return (
          <div style={{ background:T.card, border:`1px solid ${T.purple}30`, borderRadius:RADIUS.lg, padding:"16px 20px", marginBottom:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <div style={{ fontSize:15, fontWeight:700, color:T.text }}>🚀 Launch Product Risk</div>
              <button onClick={()=>setLaunchOpen(false)} style={{ background:"none", border:"none", color:T.textSub, cursor:"pointer", fontSize:16 }}>✕</button>
            </div>
            <div style={{ fontSize:12, color:T.textSub }}>No upcoming orders found. Add an order with future arrival dates to analyze launch risk.</div>
          </div>
        );
        const selOrder = futureOrders[launchOrderIdx] || futureOrders[0];
        const { events } = getOrderCashEvents(selOrder);
        const cogsTotal = events.filter(e=>e.type==="payment_term").reduce((s,e)=>s+e.amount,0);
        const shippingTotal = events.filter(e=>e.type==="shipping").reduce((s,e)=>s+e.amount,0);
        const customsTotal = events.filter(e=>e.type==="customs").reduce((s,e)=>s+e.amount,0);
        const inspectionTotal = events.filter(e=>e.type==="inspection").reduce((s,e)=>s+e.amount,0);
        const totalRisk = cogsTotal + shippingTotal + customsTotal + inspectionTotal;
        const totalLaunchUnits = (selOrder.containerBatches||[]).reduce((s,cb)=>s+(cb.unitCount||0),0) || selOrder.unitCount || 0;
        const revenuePerUnit = weightedPayout;
        const payoutDelay = invSummary.avgPayoutDelay || 14;
        const bufferThreshold = Number(months?._appSettings?.bufferThreshold) || 0;

        return (
          <div style={{ background:T.card, border:`1px solid ${T.purple}30`, borderRadius:RADIUS.lg, padding:"16px 20px", marginBottom:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div style={{ fontSize:15, fontWeight:700, color:T.text }}>🚀 Launch Product Risk</div>
              <button onClick={()=>setLaunchOpen(false)} style={{ background:"none", border:"none", color:T.textSub, cursor:"pointer", fontSize:16 }}>✕</button>
            </div>

            {/* Order selector */}
            {futureOrders.length > 1 && (
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                <span style={{ fontSize:12, color:T.textSub }}>Order:</span>
                <StyledSelect value={launchOrderIdx} onChange={e=>{ setLaunchOrderIdx(Number(e.target.value)); setLaunchMonths([]); setLaunchUnitsPerMonth({}); }}>
                  {futureOrders.map((o,i)=><option key={i} value={i}>{o.supplierName||o.name||`Order ${i+1}`} — {fmtFull(totalRisk)}</option>)}
                </StyledSelect>
              </div>
            )}

            {/* Risk breakdown */}
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:14 }}>
              {[["COGS",cogsTotal,T.red],["Shipping",shippingTotal,T.orange],["Customs",customsTotal,T.purple],["Inspection",inspectionTotal,T.accent]].map(([label,amt,col])=>
                amt > 0 ? (
                  <div key={label} style={{ flex:1, minWidth:90, padding:"8px 10px", background:col+"08", borderRadius:RADIUS.sm, textAlign:"center" }}>
                    <div style={{ fontSize:10, color:T.textSub, textTransform:"uppercase" }}>{label}</div>
                    <div style={{ fontSize:14, fontWeight:700, color:col }}>{fmtFull(amt)}</div>
                  </div>
                ) : null
              )}
              <div style={{ flex:1, minWidth:90, padding:"8px 10px", background:T.red+"10", borderRadius:RADIUS.sm, textAlign:"center", border:`1px solid ${T.red}20` }}>
                <div style={{ fontSize:10, color:T.textSub, textTransform:"uppercase", fontWeight:700 }}>Total Risk</div>
                <div style={{ fontSize:16, fontWeight:700, color:T.red }}>{fmtFull(totalRisk)}</div>
              </div>
            </div>

            {/* Month selector — pick which months launch units sell */}
            <div style={{ fontSize:12, color:T.textSub, marginBottom:6 }}>Which months will these {totalLaunchUnits.toLocaleString()} units sell?</div>
            <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:10 }}>
              {Array.from({length:12},(_,i)=>{
                const sel = launchMonths.includes(i);
                const isPast = i < TODAY.getMonth();
                return (
                  <button key={i} onClick={()=>{ if(isPast)return; setLaunchMonths(prev=>prev.includes(i)?prev.filter(x=>x!==i):[...prev,i]); }}
                    style={{ padding:"4px 8px", borderRadius:RADIUS.sm, fontSize:11, fontFamily:FONT, cursor:isPast?"default":"pointer",
                      opacity:isPast?0.4:1, background:sel?T.purple+"18":"transparent",
                      border:`1px solid ${sel?T.purple:T.border}`, color:sel?T.purple:T.textSub, fontWeight:sel?700:400, minWidth:42, textAlign:"center" }}>
                    {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][i]}
                  </button>
                );
              })}
            </div>

            {/* Per-month unit distribution */}
            {launchMonths.length > 0 && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:11, color:T.textSub, marginBottom:6 }}>Units per month (total: {totalLaunchUnits.toLocaleString()}):</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8 }}>
                  {launchMonths.sort((a,b)=>a-b).map(mi=>{
                    const k = mkKey(currentYear, mi+1);
                    const v = launchUnitsPerMonth[k] || Math.round(totalLaunchUnits / launchMonths.length);
                    return (
                      <div key={mi} style={{ display:"flex", alignItems:"center", gap:3 }}>
                        <span style={{ fontSize:11, color:T.purple, fontWeight:600, minWidth:28 }}>{["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][mi]}</span>
                        <input type="number" value={launchUnitsPerMonth[k]!=null?launchUnitsPerMonth[k]:v}
                          onChange={e=>setLaunchUnitsPerMonth(prev=>({...prev,[k]:parseInt(e.target.value)||0}))}
                          style={{ width:60, background:T.surface, border:`1px solid ${T.purple}40`, borderRadius:RADIUS.sm, color:T.text, fontSize:12, fontWeight:700, padding:"3px 6px", outline:"none", fontFamily:FONT, textAlign:"center" }}/>
                      </div>
                    );
                  })}
                </div>

                {/* Payout checkbox */}
                <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:T.textSub, cursor:"pointer", marginBottom:12 }}>
                  <input type="checkbox" checked={launchAccountPayout} onChange={e=>setLaunchAccountPayout(e.target.checked)} />
                  Account for payout delay (~{payoutDelay}d)
                </label>

                {/* Target visualization per month */}
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {launchMonths.sort((a,b)=>a-b).map(mi=>{
                    const k = mkKey(currentYear, mi+1);
                    const existingTarget = months[k]?.target || 0;
                    const launchUnits = launchUnitsPerMonth[k] != null ? launchUnitsPerMonth[k] : Math.round(totalLaunchUnits / launchMonths.length);
                    const combinedTarget = existingTarget + launchUnits;
                    const launchPct = combinedTarget > 0 ? Math.round(launchUnits / combinedTarget * 100) : 0;
                    const launchRevenue = launchUnits * revenuePerUnit;
                    const existingBarPct = combinedTarget > 0 ? Math.round(existingTarget / combinedTarget * 100) : 0;

                    return (
                      <div key={mi} style={{ flex:1, minWidth:100, padding:"8px 10px", background:T.bg, borderRadius:RADIUS.sm }}>
                        <div style={{ fontSize:11, fontWeight:700, color:T.text, marginBottom:4 }}>
                          {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][mi]} {currentYear}
                        </div>
                        {/* Stacked bar */}
                        <div style={{ height:12, borderRadius:4, overflow:"hidden", display:"flex", marginBottom:4, background:T.surface }}>
                          <div style={{ width:`${existingBarPct}%`, background:T.green, minWidth:existingTarget>0?2:0 }} />
                          <div style={{ width:`${100-existingBarPct}%`, background:T.purple+"60", backgroundImage:"repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.15) 3px, rgba(255,255,255,0.15) 6px)" }} />
                        </div>
                        <div style={{ fontSize:10, color:T.textSub }}>
                          <span style={{ color:T.purple, fontWeight:700 }}>{launchUnits.toLocaleString()}</span> of {combinedTarget.toLocaleString()} is new ({launchPct}%)
                        </div>
                        {launchAccountPayout && (
                          <div style={{ fontSize:10, color:T.accent, marginTop:2 }}>
                            Revenue arrives ~{["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][Math.min(11, mi + Math.ceil(payoutDelay/30))]}: {fmtFull(launchRevenue)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Cashflow warning */}
                {totalRisk > bufferThreshold && bufferThreshold > 0 && (
                  <div style={{ fontSize:12, color:T.red, padding:"8px 12px", background:T.red+"08", borderRadius:RADIUS.sm, marginTop:8 }}>
                    ⚠ Total launch risk ({fmtFull(totalRisk)}) exceeds your cash buffer ({fmtFull(bufferThreshold)}). Ensure you have enough runway before committing.
                  </div>
                )}
                <div style={{ fontSize:11, color:T.textSub, marginTop:6, lineHeight:1.5 }}>
                  Break-even for this launch: {revenuePerUnit > 0 ? `${Math.ceil(totalRisk / revenuePerUnit).toLocaleString()} units at ${currSym()}${revenuePerUnit.toFixed(2)}/unit` : "set marketplace payouts to calculate"}.
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Target vs Actual Chart — full width, transparent ── */}
      {!isMobile && (() => {
        const isWeekView = chartView === "week";
        const yr = chartYear;

        // Build chart data points — uses viewMonths for month view when "from today"
        const chartViewMonths = kpiFrom === "today" && !isWeekView
          ? viewMonths
          : Array.from({length:12},(_,i)=>({year:yr,month:i}));
        // Helper: resolve the effective target for a given month key,
        // Alias the page-level resolver for readability within the chart IIFE.
        const resolveTarget = (mKey) => {
          return resolveTargetForKey(mKey);
        };

        const chartData = isWeekView
          ? (()=>{
              const weekStart = kpiFrom === "today" ? new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() - TODAY.getDay()) : new Date(yr, 0, 1);
              const todayMs = TODAY.getTime();
              return Array.from({length:52},(_,w)=>{
                const d = new Date(weekStart.getTime() + w * 7 * 86400000);
                const dEnd = new Date(d.getTime() + 6 * 86400000);
                const mIdx = d.getMonth();
                const wYear = d.getFullYear();
                const mKey = mkKey(wYear, mIdx+1);
                // Use actual days-in-month for precise weekly conversion instead
                // of the fixed 4.33 divisor. This ensures cumulative weekly targets
                // over a month exactly equal the monthly target — no rounding drift
                // that was causing different stockout dates between views.
                const dim = new Date(wYear, mIdx+1, 0).getDate();
                const rawWeekT = Math.round(resolveTarget(mKey) * 7 / dim);
                const wPct = simOpen?(simMode==="all"?simPct:(simCustom[mKey]||0)):0;
                const weekTarget = Math.round(rawWeekT*(1+wPct/100));
                const isPastWeek = dEnd.getTime() < todayMs;
                const isCurrentWeek = d.getTime() <= todayMs && dEnd.getTime() >= todayMs;
                const monthActual = Math.round((months[mKey]?.actualSold || 0) * _selPct);
                const weekActual = (isPastWeek || isCurrentWeek) && monthActual > 0 ? Math.round(monthActual * 7 / dim) : null;
                const mNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                const dateRange = d.getMonth()===dEnd.getMonth()
                  ? `${mNames[d.getMonth()]} ${d.getDate()}-${dEnd.getDate()}`
                  : `${mNames[d.getMonth()]} ${d.getDate()}-${dEnd.getDate()}`;
                const label = `W${w+1}: ${dateRange}`;
                return { name:label, target:weekTarget, actual:weekActual>0?weekActual:null, _mIdx:mIdx, _year:wYear };
              });
            })()
          : chartViewMonths.map((vm,i)=>{
              const k = mkKey(vm.year, vm.month+1);
              const rawT = resolveTarget(k);
              const mPct = simOpen?(simMode==="all"?simPct:(simCustom[k]||0)):0;
              const t = Math.round(rawT*(1+mPct/100));
              const a = Math.round((months[k]?.actualSold || 0) * _selPct);
              const isPast = vm.year<currentYear||(vm.year===currentYear&&vm.month<TODAY.getMonth());
              const isCur = vm.year===currentYear&&vm.month===TODAY.getMonth();
              const label = vm.year !== currentYear ? `${MONTH_NAMES[vm.month].slice(0,3)}'${String(vm.year).slice(2)}` : MONTH_NAMES[vm.month].slice(0,3);
              return { name: label, target: t, actual: (isPast||isCur) && a > 0 ? a : null, _mIdx:vm.month, _year:vm.year };
            });

        // Stock prediction line — computed at DAILY granularity internally,
        // then sampled at end-of-period for display. This gives:
        //   - Smooth declining lines (no staircase in week-view)
        //   - Identical stockout/below-target dates in BOTH views
        //   - Arrivals land on the exact day they occur
        //
        // The anchor comes from effectiveStartingStock (pool > mkt > product
        // > API cascade). Days at or before today are flat at the anchor.
        // Days after today deplete at the daily sales rate derived from the
        // resolved monthly target (marketplace/pool/sim-aware).
        // Declared outside showInventory so Customized component can read them
        const belowTargetDates = [];
        const stockoutDates = [];
        const reorderDates = [];

        if (showInventory) {
          const stockAnchor = effectiveStartingStock || todayInventory || 0;
          const futureArrivals = stockArrivals.filter(arr => arr.date > TODAY);

          const arrivalByDay = {};
          for (const arr of futureArrivals) {
            const dk = `${arr.date.getFullYear()}-${arr.date.getMonth()+1}-${arr.date.getDate()}`;
            arrivalByDay[dk] = (arrivalByDay[dk] || 0) + arr.units;
          }

          // Walk day-by-day from the start of the chart through the end.
          // Store stock level keyed by "YYYY-M-D" for O(1) lookup when sampling.
          const dailyStock = {};
          const todayMs = TODAY.getTime();

          // Determine the date range from chartData
          const firstPt = chartData[0];
          const lastPt = chartData[chartData.length - 1];
          const startDate = isWeekView
            ? new Date(firstPt._year, firstPt._mIdx, 1)
            : new Date(firstPt._year, firstPt._mIdx, 1);
          const endDate = isWeekView
            ? new Date(lastPt._year, lastPt._mIdx + 1, 0) // last day of last month
            : new Date(lastPt._year, lastPt._mIdx + 1, 0);

          // BACKWARD reconstruction for past dates: walk from today backward
          // to the chart start. Each past day: stock INCREASES (we're undoing
          // sales and removing arrivals). This shows what the stock level WAS
          // in the past based on what we know today.
          //   past_stock[day] = today_stock + sales_between(day, today) − arrivals_between(day, today)
          // Build a set of ALL arrivals (not just future) for backward walk.
          const allArrivalsByDay = {};
          for (const arr of stockArrivals) {
            const adk = `${arr.date.getFullYear()}-${arr.date.getMonth()+1}-${arr.date.getDate()}`;
            allArrivalsByDay[adk] = (allArrivalsByDay[adk] || 0) + arr.units;
          }
          // Walk backward from today to chart start
          let pastStock = stockAnchor;
          const backCursor = new Date(TODAY);
          backCursor.setDate(backCursor.getDate() - 1); // start from yesterday
          while (backCursor >= startDate) {
            const bcy = backCursor.getFullYear(), bcm = backCursor.getMonth() + 1, bcd = backCursor.getDate();
            const bdk = `${bcy}-${bcm}-${bcd}`;
            const bmk = mkKey(bcy, bcm);
            const bdim = new Date(bcy, bcm, 0).getDate();
            const bTarget = resolveTarget(bmk);
            const bDailyRate = bTarget / bdim;
            // Undo: add back the sales that happened on this day, remove arrivals
            pastStock += bDailyRate;
            pastStock -= (allArrivalsByDay[bdk] || 0);
            pastStock = Math.max(0, pastStock);
            dailyStock[bdk] = pastStock;
            backCursor.setDate(backCursor.getDate() - 1);
          }
          // Today = anchor
          const todayDk = `${TODAY.getFullYear()}-${TODAY.getMonth()+1}-${TODAY.getDate()}`;
          dailyStock[todayDk] = stockAnchor;

          // FORWARD projection for future dates
          let stock = stockAnchor;
          let prevStock = stockAnchor;
          let wasBelow = false; // edge detection: track previous state
          let wasZero = false;
          const cursor = new Date(TODAY);
          cursor.setDate(cursor.getDate() + 1); // start from tomorrow
          while (cursor <= endDate) {
            const cy = cursor.getFullYear(), cm = cursor.getMonth() + 1, cd = cursor.getDate();
            const dk = `${cy}-${cm}-${cd}`;
              const mk = mkKey(cy, cm);
              const dim = new Date(cy, cm, 0).getDate();
              const baseTarget = resolveTarget(mk);
              const simMod = simOpen ? (simMode === "all" ? simPct : (simCustom[mk] || 0)) : 0;
              const monthTarget = Math.round(baseTarget * (1 + simMod / 100));
              const dailyRate = monthTarget / dim;

              prevStock = stock;
              stock += (arrivalByDay[dk] || 0);
              stock -= dailyRate;
              stock = Math.max(0, stock);
              dailyStock[dk] = stock;

              // Edge detection: capture each transition into "below target"
              // Compare stock against REMAINING demand for the rest of this
              // month, not the full month target. This avoids false positives
              // when stock dips below the full-month number but still covers
              // the remaining days comfortably.
              const remainingDays = dim - cursor.getDate();
              const remainingDemand = dailyRate * remainingDays;
              const isBelow = stock < remainingDemand && remainingDemand > 0;
              // Only record insufficient dates BEFORE the first stockout —
              // once stock hits zero, "insufficient" is redundant.
              if (isBelow && !wasBelow && stockoutDates.length === 0) {
                belowTargetDates.push(new Date(cursor));
              }
              wasBelow = isBelow;

              // Edge detection: capture each transition into "zero stock"
              if (prevStock > 0 && stock <= 0) {
                stockoutDates.push(new Date(cursor));
              }
            cursor.setDate(cursor.getDate() + 1);
          }

          // Reorder dates = each stockout minus lead time. If the reorder
          // date is in the past, it stays (rendered at chart left edge
          // with an "Overdue" label).
          if (effectiveLeadTime > 0) {
            for (const soDate of stockoutDates) {
              reorderDates.push(new Date(soDate.getTime() - effectiveLeadTime * 86400000));
            }
          }

          // Sample: for each chart data point, pick the END-of-period stock.
          // End-of-period = industry standard ("how much is LEFT after this period").
          chartData.forEach(d => {
            let sampleDate;
            if (isWeekView) {
              // Week-view: d is positioned at the week's START date (d._mIdx is
              // the start month). Sample at the END of the week (6 days later).
              // We stored _mIdx as the month of the week-start. The actual end
              // date is weekStart + w*7 + 6 days — reconstruct from the data.
              const weekStartDate = kpiFrom === "today"
                ? new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() - TODAY.getDay())
                : new Date(yr, 0, 1);
              const idx = chartData.indexOf(d);
              const dEnd = new Date(weekStartDate.getTime() + idx * 7 * 86400000 + 6 * 86400000);
              sampleDate = dEnd;
            } else {
              // Month-view: sample at the last day of the month
              sampleDate = new Date(d._year, d._mIdx + 1, 0);
            }
            const sk = `${sampleDate.getFullYear()}-${sampleDate.getMonth()+1}-${sampleDate.getDate()}`;
            d.inventory = dailyStock[sk] ?? stockAnchor;
          });
          // Store anchor on first point so the tooltip can show correct "Starting stock"
          if (chartData.length > 0) chartData[0]._stockAnchor = stockAnchor;
        }

        const sortedYears = visibleYears.slice().sort();
        const pastYears = sortedYears.filter(y=>y<=currentYear);

        // Diamond data — in-stock dates from order batches (fallback: arrival date, then order date)
        const diamonds = [];
        if (showOrders) {
          for (const order of (orders||[])) {
            if (selectedOrderId !== "all" && order.id !== selectedOrderId) continue;
            const oColor = (ORDER_COLORS[orders.indexOf(order) % ORDER_COLORS.length] || {}).border || "#8b5cf6";
            const batches = order.containerBatches || [];
            if (batches.length === 0) {
              // Simple order without batches — use order date
              const oDate = parseDate(order.orderDate);
              if (oDate && oDate.getFullYear()===yr) {
                const mIdx = oDate.getMonth();
                const dayOfYear = Math.floor((oDate - new Date(yr,0,1)) / 86400000);
                diamonds.push({ monthIdx:mIdx, weekIdx:Math.floor(dayOfYear/7), date:oDate, color:oColor,
                  units:order.unitCount||0, containers:0, departure:"—", arrival:"—",
                  inStock:formatDate(oDate, dateFormat), orderName:order.number||order.id.slice(0,6), batchIdx:0 });
              }
            } else {
              for (const cb of batches) {
                let inStockD = null;
                // Try in-stock date first
                if (cb.inStockDate) inStockD = parseDate(cb.inStockDate);
                // Fallback: arrival + days
                if (!inStockD && cb.arrivalDate) {
                  const arr = parseDate(cb.arrivalDate);
                  if (arr) inStockD = new Date(arr.getTime() + (cb.inStockDays||0)*86400000);
                }
                // Fallback: departure date
                if (!inStockD && cb.departureDate) inStockD = parseDate(cb.departureDate);
                if (inStockD && inStockD.getFullYear()===yr) {
                  const mIdx = inStockD.getMonth();
                  const dayOfYear = Math.floor((inStockD - new Date(yr,0,1)) / 86400000);
                  const weekIdx = Math.floor(dayOfYear / 7);
                  const totalUnits = cb.unitCount || Math.round((order.unitCount||0) * (cb.containers||1) / Math.max(1,batches.reduce((s,b)=>s+(b.containers||0),0)));
                  diamonds.push({
                    monthIdx: mIdx, weekIdx, date: inStockD, color: oColor,
                    units: totalUnits, containers: cb.containers||0,
                    departure: cb.departureDate||"—", arrival: cb.arrivalDate||"—",
                    inStock: cb.inStockDate ? formatDate(cb.inStockDate, dateFormat) : formatDate(inStockD, dateFormat),
                    orderName: order.number || order.id.slice(0,6),
                    batchIdx: batches.indexOf(cb)+1,
                  });
                }
              }
            }
          }
        }

        // Orders missing in-stock dates
        const missingInStock = [];
        for (const order of (orders||[])) {
          for (const cb of (order.containerBatches||[])) {
            if (!cb.inStockDate && !cb.arrivalDate) {
              missingInStock.push({ orderId:order.id, orderName:order.number||order.id.slice(0,6), batchIdx:(order.containerBatches||[]).indexOf(cb)+1 });
            }
          }
        }

        const chartBlock = (
          <div ref={chartContainerRef} data-tour="targets-chart" style={chartFullscreen
            ? { position:"fixed", inset:0, zIndex:9998, background:T.bg, padding:16, display:"flex", flexDirection:"column", overflow:"hidden" }
            : { border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, padding:"12px 16px 8px", marginBottom:16, background:"transparent" }}>
            {/* Header row — all controls inline */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8, flexWrap:"wrap", gap:6, flexShrink:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:13, fontWeight:700, color:T.text }}>📊 Target vs Actual</span><Info tip="targetsChart"/>
                {/* Auto-computed stock display when Stock toggled */}
                {showInventory && (effectiveStartingStock || todayInventory) > 0 && (
                  <Tooltip content="Current units in stock — resolved from the pool/marketplace/product hierarchy. Edit stock on the Marketplaces > Pools tab."><div style={{ display:"flex", alignItems:"center", gap:4 }}>
                    <span style={{ fontSize:11, color:T.teal }}>📦</span>
                    <span style={{ fontSize:12, fontWeight:700, color:T.teal, fontFamily:FONT }}>{(effectiveStartingStock||todayInventory||0).toLocaleString()}</span>
                  </div></Tooltip>
                )}
              </div>
              <div data-tour="targets-chart-controls" style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"nowrap" }}>
                <div style={{ display:"flex", border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, overflow:"hidden", flexShrink:0 }}>
                  {[["month","Month"],["week","Week"]].map(([v,l])=>(
                    <button key={v} onClick={()=>setChartView(v)} style={{ padding:"3px 10px", border:"none", cursor:"pointer", fontSize:12, fontFamily:FONT, fontWeight:chartView===v?700:400, background:chartView===v?T.accent:"transparent", color:chartView===v?"#fff":T.textSub, whiteSpace:"nowrap" }}>{l}</button>
                  ))}
                </div>
                <Tooltip content="Toggle stock/inventory projection line. Enter your current stock and see how it depletes over time based on your targets. Stock replenishes when order batches arrive (in-stock dates)."><button onClick={()=>setShowInventory(!showInventory)}  style={{ padding:"3px 10px", borderRadius:RADIUS.sm, fontSize:11, fontFamily:FONT, cursor:"pointer", fontWeight:showInventory?700:400, background:showInventory?T.teal+"22":"transparent", border:`1px solid ${showInventory?T.teal:T.border}`, color:showInventory?T.teal:T.textSub, whiteSpace:"nowrap", flexShrink:0, display:"inline-flex", alignItems:"center", gap:4 }}>📦 Stock</button></Tooltip>
                {/* Styled order dropdown */}
                <div style={{ position:"relative", flexShrink:0 }} ref={orderDropRef}>
                  <button onClick={()=>setOrderDropOpen(!orderDropOpen)}
                    style={{ padding:"3px 10px", borderRadius:RADIUS.sm, fontSize:11, fontFamily:FONT, cursor:"pointer", fontWeight:showOrders?700:400, background:showOrders?T.blue+"18":"transparent", border:`1px solid ${showOrders?T.blue:T.border}`, color:showOrders?T.blue:T.textSub, display:"inline-flex", alignItems:"center", gap:4, whiteSpace:"nowrap" }}>
                    🚢 {showOrders ? (selectedOrderId==="all" ? "All Orders" : (orders||[]).find(o=>o.id===selectedOrderId)?.number || "Order") : "Orders"}
                    <span style={{ fontSize:12, opacity:0.6 }}>▼</span>
                  </button>
                  {orderDropOpen && (
                    <div style={{ position:"absolute", top:"calc(100% + 4px)", right:0, zIndex:600, background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg, boxShadow:"0 12px 40px rgba(0,0,0,0.35)", minWidth:160, overflow:"hidden", animation:"dashMenuIn 0.15s ease both" }}>
                      <button onClick={()=>{setShowOrders(false);setOrderDropOpen(false);}}
                        style={{ display:"block", width:"100%", padding:"9px 14px", background:!showOrders?T.accent+"18":"transparent", border:"none", cursor:"pointer", fontFamily:FONT, fontSize:12, color:!showOrders?T.accent:T.text, textAlign:"left", fontWeight:!showOrders?700:400 }}>
                        🚫 Orders Off
                      </button>
                      <button onClick={()=>{setShowOrders(true);setSelectedOrderId("all");setOrderDropOpen(false);}}
                        style={{ display:"block", width:"100%", padding:"9px 14px", background:showOrders&&selectedOrderId==="all"?T.blue+"18":"transparent", border:"none", cursor:"pointer", fontFamily:FONT, fontSize:12, color:showOrders&&selectedOrderId==="all"?T.blue:T.text, textAlign:"left", fontWeight:showOrders&&selectedOrderId==="all"?700:400 }}>
                        🚢 All Orders
                      </button>
                      {(orders||[]).map((o,idx)=>{
                        const c = ORDER_COLORS[idx % ORDER_COLORS.length];
                        const cl = c?.border || c || "#8b5cf6";
                        const sel = showOrders && selectedOrderId===o.id;
                        return <button key={o.id} onClick={()=>{setShowOrders(true);setSelectedOrderId(o.id);setOrderDropOpen(false);}}
                          style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"9px 14px", background:sel?cl+"18":"transparent", border:"none", cursor:"pointer", fontFamily:FONT, fontSize:12, color:sel?cl:T.text, textAlign:"left", fontWeight:sel?700:400 }}>
                          <div style={{ width:8, height:8, borderRadius:2, background:cl, transform:"rotate(45deg)", flexShrink:0 }}/>
                          {o.number||o.id.slice(0,6)}
                        </button>;
                      })}
                    </div>
                  )}
                </div>
                <StyledSelect value={chartYear} onChange={e=>setChartYear(parseInt(e.target.value))}>
                  {pastYears.map(y=><option key={y} value={y}>{y}{y===currentYear?" (Current)":""}</option>)}
                </StyledSelect>
                <button onClick={()=>setChartFullscreen(f=>!f)} title={chartFullscreen?"Minimize (Esc)":"Full screen"} style={{ padding:"3px 8px", borderRadius:RADIUS.sm, fontSize:13, fontFamily:FONT, cursor:"pointer", background:chartFullscreen?T.accent+"18":"transparent", border:`1px solid ${chartFullscreen?T.accent:T.border}`, color:chartFullscreen?T.accent:T.textSub, lineHeight:1 }}>{chartFullscreen?"⊙":"⛶"}</button>
              </div>
            </div>

            {/* Missing in-stock date warnings */}
            {showOrders && missingInStock.length > 0 && (
              <div style={{ marginBottom:8, padding:"6px 10px", background:T.orange+"12", border:`1px solid ${T.orange}33`, borderRadius:RADIUS.sm, fontSize:11, color:T.orange }}>
                ⚠ {missingInStock.length} batch{missingInStock.length>1?"es":""} missing in-stock dates: {missingInStock.slice(0,3).map(m=>`${m.orderName} B${m.batchIdx}`).join(", ")}{missingInStock.length>3?` +${missingInStock.length-3} more`:""}.
                <button onClick={()=>{ window.location.hash="orders"; }} style={{ background:"none", border:"none", color:T.orange, textDecoration:"underline", cursor:"pointer", fontFamily:FONT, fontSize:11, fontWeight:700, marginLeft:4 }}>Fix in Orders →</button>
              </div>
            )}

            {/* Chart legend */}
            <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:6, fontSize:10, color:T.textSub, flexShrink:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:4 }}><div style={{ width:20, height:0, borderTop:"2px dashed "+T.accent }}/> Target</div>
              <div style={{ display:"flex", alignItems:"center", gap:4 }}><div style={{ width:20, height:2.5, background:T.green, borderRadius:1 }}/> Actual</div>
              {showInventory && <div style={{ display:"flex", alignItems:"center", gap:4 }}><div style={{ width:20, height:0, borderTop:"2px dashed "+T.teal }}/> Stock</div>}
              {showOrders && <div style={{ display:"flex", alignItems:"center", gap:4 }}><div style={{ width:8, height:8, background:T.blue, transform:"rotate(45deg)", borderRadius:1 }}/> In-Stock ({diamonds.length})</div>}
              <div style={{ display:"flex", alignItems:"center", gap:4 }}><div style={{ width:14, height:0, borderTop:`1.5px dashed ${T.green}` }}/> Today</div>
              {showInventory && <div style={{ display:"flex", alignItems:"center", gap:4 }}><div style={{ width:14, height:2.5, background:"#FF9500", borderRadius:1 }}/> Reorder</div>}
              {showInventory && <div style={{ display:"flex", alignItems:"center", gap:4 }}><div style={{ width:14, height:0, borderTop:"1.5px dashed #FF9500" }}/> Insufficient</div>}
              {showInventory && <div style={{ display:"flex", alignItems:"center", gap:4 }}><div style={{ width:14, height:0, borderTop:"1.5px dashed #FF3B30" }}/> Stockout</div>}
            </div>

            {/* Chart */}
            <div style={chartFullscreen ? { position:"relative", flex:"1 1 0%", minHeight:0, overflow:"hidden" } : { position:"relative" }}>
              <ResponsiveContainer width="100%" height={chartFullscreen ? "100%" : chartH - 80}>
                <LineChart data={chartData} margin={{top:12,right:8,bottom:0,left:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.border+"33"} vertical={false}/>
                  <XAxis dataKey="name" tick={{fontSize:12,fill:T.textSub}} axisLine={false} tickLine={false} interval={isWeekView?3:0}/>
                  <YAxis tick={{fontSize:11,fill:T.textSub}} axisLine={false} tickLine={false} width={55} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(v>=10000?0:1)}k` : String(v)}/>
                  <RTooltip
                    contentStyle={{background:T.card,border:`1px solid ${T.border}`,borderRadius:8,fontSize:12,fontFamily:FONT}}
                    labelStyle={{color:T.text,fontWeight:700}}
                    itemStyle={{padding:0}}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const pt = payload[0]?.payload;
                      if (!pt) return null;
                      const idx = chartData.indexOf(pt);
                      const prevStock = idx > 0 ? chartData[idx-1]?.inventory : (pt._stockAnchor ?? pt.inventory);
                      return (
                        <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:8, padding:"10px 14px", fontSize:12, fontFamily:FONT, boxShadow:T.shadow }}>
                          <div style={{ fontWeight:700, color:T.text, marginBottom:6 }}>{label}</div>
                          <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                            <div><span style={{ color:T.accent }}>Target:</span> <strong style={{ color:T.text }}>{(pt.target||0).toLocaleString()}</strong> units</div>
                            {pt.actual != null && <div><span style={{ color:T.green }}>Actual:</span> <strong style={{ color:T.text }}>{pt.actual.toLocaleString()}</strong> units</div>}
                            {showInventory && pt.inventory != null && <>
                              <div style={{ borderTop:`1px solid ${T.border}`, marginTop:3, paddingTop:3 }}>
                                <span style={{ color:T.teal }}>Starting stock:</span> <strong style={{ color:T.text }}>{Math.round(prevStock ?? pt.inventory).toLocaleString()}</strong>
                              </div>
                              <div>
                                <span style={{ color:T.teal }}>Ending stock:</span> <strong style={{ color:T.text }}>{Math.round(pt.inventory).toLocaleString()}</strong>
                              </div>
                            </>}
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Line type="linear" dataKey="target" stroke={T.accent} strokeWidth={2} strokeDasharray="6 3" dot={false} name="Target"/>
                  <Line type="linear" dataKey="actual" stroke={T.green} strokeWidth={2.5} dot={{r:3,fill:T.green}} connectNulls={false} name="Actual"/>
                  {showInventory && <Line type="linear" dataKey="inventory" stroke={T.teal} strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="Stock"/>}

                  {/* Always-present Customized: Today line + diamond markers */}
                  <Customized component={({ xAxisMap, yAxisMap }) => {
                    const xAxis = xAxisMap && (xAxisMap[0] || Object.values(xAxisMap)[0]);
                    const yAxis = yAxisMap && (yAxisMap[0] || Object.values(yAxisMap)[0]);
                    if (!xAxis?.scale || !yAxis?.scale) return null;
                    const xs = xAxis.scale;
                    const ys = yAxis.scale;
                    const bw = xs.bandwidth ? xs.bandwidth()/2 : 0;
                    const yRange = ys.range ? ys.range() : [160,0];
                    const chartTopPx = Math.min(...yRange);
                    const chartBotPx = Math.max(...yRange);

                    // Today vertical line
                    const todayIdx = isWeekView
                      ? (kpiFrom==="today" ? 0 : Math.min(51, Math.floor((Math.floor((TODAY - new Date(yr,0,1)) / 86400000)) / 7)))
                      : (kpiFrom==="today" ? 0 : TODAY.getMonth());
                    const todayDp = yr === currentYear || kpiFrom==="today" ? chartData[todayIdx] : null;
                    const todayX = todayDp ? xs(todayDp.name) + bw : null;

                    // Stockout indicators
                    const reorderLines = []; // orange solid thick: must reorder by this date
                    const belowTargetLines = []; // orange dashed: inventory drops below target
                    const stockoutLines = []; // red dashed: stock hits zero
                    const zeroSegments = []; // horizontal thick red lines where stock = 0 for 2+ points
                    const zeroDots = []; // single red dots where stock = 0 for exactly 1 point
                    if (showInventory) {
                      // Crossing dates are pre-computed from the DAILY stock walk
                      // (belowTargetDates / stockoutDates). This guarantees the
                      // EXACT SAME calendar date in both week and month views —
                      // no more view-dependent interpolation from chart data points.

                      // Helper: find the X position on the chart for a calendar date
                      const dateToX = (calDate) => {
                        if (!calDate) return null;
                        // Find the two chart data points that bracket this date
                        const dMs = calDate.getTime();
                        for (let si = 0; si < chartData.length; si++) {
                          const dp = chartData[si];
                          // Approximate: each point sits at mid-month or mid-week
                          let ptDate;
                          if (isWeekView) {
                            const weekStartDate = kpiFrom === "today"
                              ? new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() - TODAY.getDay())
                              : new Date(yr, 0, 1);
                            ptDate = new Date(weekStartDate.getTime() + si * 7 * 86400000 + 3 * 86400000); // mid-week
                          } else {
                            ptDate = new Date(dp._year, dp._mIdx, 15); // mid-month
                          }
                          if (ptDate.getTime() >= dMs && si > 0) {
                            // Interpolate between prev and current
                            let prevDate;
                            if (isWeekView) {
                              const weekStartDate = kpiFrom === "today"
                                ? new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate() - TODAY.getDay())
                                : new Date(yr, 0, 1);
                              prevDate = new Date(weekStartDate.getTime() + (si-1) * 7 * 86400000 + 3 * 86400000);
                            } else {
                              prevDate = new Date(chartData[si-1]._year, chartData[si-1]._mIdx, 15);
                            }
                            const ratio = (dMs - prevDate.getTime()) / Math.max(1, ptDate.getTime() - prevDate.getTime());
                            const prevX = xs(chartData[si-1].name) + bw;
                            const curX = xs(dp.name) + bw;
                            return prevX + (curX - prevX) * Math.min(1, Math.max(0, ratio));
                          }
                        }
                        // Past the end — place at last point
                        if (chartData.length > 0) return xs(chartData[chartData.length-1].name) + bw;
                        return null;
                      };

                      for (const rd of reorderDates) {
                        const isPast = rd <= TODAY;
                        const x = isPast ? (xs(chartData[0]?.name) + bw) : dateToX(rd);
                        if (x != null && !isNaN(x)) {
                          const lbl = isPast
                            ? `Reorder overdue!`
                            : `Reorder by ${formatDate(rd, dateFormat)}`;
                          reorderLines.push({ x, label: lbl, isPast });
                        }
                      }
                      for (const btd of belowTargetDates) {
                        const x = dateToX(btd);
                        if (x != null && !isNaN(x)) {
                          belowTargetLines.push({ x, label: `Insufficient ${formatDate(btd, dateFormat)}` });
                        }
                      }
                      for (const sod of stockoutDates) {
                        const x = dateToX(sod);
                        if (x != null && !isNaN(x)) {
                          stockoutLines.push({ x, label: `Stockout ${formatDate(sod, dateFormat)}` });
                        }
                      }

                      // 2) Zero-stock visualization: find runs of consecutive 0-inventory points
                      let runStart = -1;
                      for (let si = 0; si < chartData.length; si++) {
                        const isZero = chartData[si].inventory != null && chartData[si].inventory <= 0;
                        if (isZero && runStart < 0) runStart = si;
                        if ((!isZero || si === chartData.length - 1) && runStart >= 0) {
                          const runEnd = isZero ? si : si - 1;
                          const len = runEnd - runStart + 1;
                          if (len === 1) {
                            // Single zero point → red dot
                            const dp = chartData[runStart];
                            zeroDots.push({ x: xs(dp.name) + bw, y: ys(0) });
                          } else {
                            // 2+ consecutive zero points → thick red horizontal line
                            const startDp = chartData[runStart], endDp = chartData[runEnd];
                            zeroSegments.push({
                              x1: xs(startDp.name) + bw, x2: xs(endDp.name) + bw, y: ys(0),
                            });
                          }
                          runStart = -1;
                        }
                      }
                    }

                    return (
                      <g>
                        {/* Reorder deadline → thick solid orange vertical line */}
                        {reorderLines.map((rl,ri)=>(
                          !isNaN(rl.x) && <g key={"ro"+ri}>
                            <line x1={rl.x} y1={chartTopPx} x2={rl.x} y2={chartBotPx} stroke={rl.isPast ? "#FF3B30" : "#FF9500"} strokeWidth={2.5} opacity={0.6}/>
                            <text x={rl.x} y={chartTopPx - 4} fill={rl.isPast ? "#FF3B30" : "#FF9500"} fontSize={9} fontWeight={800} textAnchor="middle" dominantBaseline="auto" opacity={0.95}>{rl.label}</text>
                          </g>
                        ))}
                        {/* Inventory insufficient → orange dashed vertical line */}
                        {belowTargetLines.map((bt,bi)=>(
                          !isNaN(bt.x) && <g key={"bt"+bi}>
                            <line x1={bt.x} y1={chartTopPx + 14} x2={bt.x} y2={chartBotPx} stroke="#FF9500" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.7}/>
                            <text x={bt.x} y={chartTopPx + 6} fill="#FF9500" fontSize={9} fontWeight={800} textAnchor="middle" dominantBaseline="hanging" opacity={0.95}>{bt.label}</text>
                          </g>
                        ))}
                        {/* Stock = 0 for 2+ consecutive points → thick red horizontal segment */}
                        {zeroSegments.map((seg,si)=>(
                          <line key={"zseg"+si} x1={seg.x1} y1={seg.y} x2={seg.x2} y2={seg.y} stroke="#FF3B30" strokeWidth={3} opacity={0.7} strokeLinecap="round"/>
                        ))}
                        {/* Stock = 0 for exactly 1 point → red dot */}
                        {zeroDots.map((dot,di)=>(
                          <circle key={"zdot"+di} cx={dot.x} cy={dot.y} r={4.5} fill="#FF3B30" opacity={0.8} stroke="#fff" strokeWidth={1}/>
                        ))}
                        {/* Stock hits zero → vertical red dashed line (label below Insufficient) */}
                        {stockoutLines.map((sl,si)=>(
                          !isNaN(sl.x) && <g key={"so"+si}>
                            <line x1={sl.x} y1={chartTopPx + 22} x2={sl.x} y2={chartBotPx} stroke="#FF3B30" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.7}/>
                            <text x={sl.x} y={chartTopPx + 20} fill="#FF3B30" fontSize={9} fontWeight={800} textAnchor="middle" dominantBaseline="hanging" opacity={0.95}>{sl.label}</text>
                          </g>
                        ))}
                        {/* Today line */}
                        {todayX != null && !isNaN(todayX) && (
                          <>
                            <line x1={todayX} y1={chartTopPx} x2={todayX} y2={chartBotPx} stroke={T.green} strokeWidth={1.5} strokeDasharray="4 3" opacity={0.6}/>
                            <text x={todayX} y={chartTopPx - 3} fill={T.green} fontSize={8} fontWeight={700} textAnchor="middle" opacity={0.8}>Today</text>
                          </>
                        )}
                        {/* Diamond markers on stock/target line */}
                        {showOrders && diamonds.map((d,i) => {
                          const rawIdx = isWeekView ? d.weekIdx : d.monthIdx;
                          // In "from today" mode, find closest match in chartData
                          const idx = kpiFrom==="today"
                            ? chartData.findIndex(cd => cd._mIdx === (isWeekView ? d.monthIdx : rawIdx) && cd._year === (d.year || yr))
                            : rawIdx;
                          const dp = chartData[idx];
                          if (!dp) return null;
                          const xLabel = dp.name;
                          const yVal = showInventory && dp.inventory != null ? dp.inventory : dp.target;
                          const cx = xs(xLabel) + bw;
                          const cy = ys(yVal);
                          if (cx == null || cy == null || isNaN(cx) || isNaN(cy)) return null;
                          const nearTop = cy < chartTopPx + 20;
                          const diamondY = nearTop ? cy + 14 : cy - 14;
                          const sz = 6;
                          const col = d.color || "#8b5cf6";
                          return (
                            <g key={"dia-"+i}
                              onMouseEnter={(e)=>{
                                const r = e.currentTarget.getBoundingClientRect();
                                setHoveredDiamond({...d, px:r.left+r.width/2, py:r.top});
                              }}
                              onMouseLeave={()=>setHoveredDiamond(null)}
                              style={{cursor:"pointer"}}>
                              <line x1={cx} y1={cy} x2={cx} y2={diamondY} stroke={col} strokeWidth={1} strokeDasharray="2 2" opacity={0.5}/>
                              <rect x={cx-sz} y={diamondY-sz} width={sz*2} height={sz*2}
                                fill={col} rx={1.5} stroke="rgba(255,255,255,0.3)" strokeWidth={0.5}
                                transform={`rotate(45,${cx},${diamondY})`}/>
                              <rect x={cx-14} y={diamondY-14} width={28} height={28} fill="transparent"/>
                            </g>
                          );
                        })}
                      </g>
                    );
                  }}/>
                </LineChart>
              </ResponsiveContainer>

              {/* Diamond tooltip — styled, instant, positioned */}
              {hoveredDiamond && (
                <div style={{ position:"fixed",
                  left: Math.min(hoveredDiamond.px, window.innerWidth - 200),
                  top: Math.max(8, hoveredDiamond.py - 8),
                  transform:`translate(-50%,${hoveredDiamond.py < 140 ? "8px" : "-100%"})`,
                  zIndex:900, pointerEvents:"none",
                  background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg,
                  padding:"10px 14px", boxShadow:"0 8px 32px rgba(0,0,0,0.4)",
                  fontSize:12, fontFamily:FONT, color:T.text, whiteSpace:"nowrap", maxWidth:280 }}>
                  <div style={{ fontWeight:700, marginBottom:6, color:hoveredDiamond.color }}>
                    {hoveredDiamond.orderName} {hoveredDiamond.batchIdx > 0 ? `Batch ${hoveredDiamond.batchIdx}` : ""}
                  </div>
                  <div style={{ display:"flex", gap:12, fontSize:11, color:T.textSub, marginBottom:6 }}>
                    <span>📦 {hoveredDiamond.units} units</span>
                    {hoveredDiamond.containers > 0 && <span>📦 {hoveredDiamond.containers} CT</span>}
                  </div>
                  <div style={{ fontSize:10, color:T.textMuted, lineHeight:1.7 }}>
                    <div>🚢 Departure: {hoveredDiamond.departure}</div>
                    <div>📦 Arrival: {hoveredDiamond.arrival}</div>
                    <div>✅ In-stock: {hoveredDiamond.inStock}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
        return chartFullscreen ? createPortal(chartBlock, document.body) : chartBlock;
      })()}

      {/* Copy targets confirmation */}
      <ConfirmSheet
        open={!!copyConfirm}
        icon="📋"
        title={copyConfirm ? `Copy ${copyConfirm.fromYear} → ${copyConfirm.toYear}?` : ""}
        message={copyConfirm ? `This will overwrite all ${copyConfirm.toYear} targets with ${copyConfirm.fromYear} values${copyConfirm.growthPct ? ` + ${copyConfirm.growthPct}% growth` : ""}. This cannot be undone.` : ""}
        confirmLabel="Yes, Copy"
        onConfirm={()=>{
          if (!copyConfirm) return;
          const {fromYear, toYear, growthPct} = copyConfirm;
          const mult = 1 + (growthPct||0)/100;
          const updates = {...months};
          for (let i=1;i<=12;i++){
            const fromK = mkKey(fromYear,i), toK = mkKey(toYear,i);
            const src = months[fromK]?.target||0;
            updates[toK] = {...(updates[toK]||{}), target: Math.round(src*mult)};
          }
          monthOps.setMonthsDirect(updates);
          setCopyConfirm(null);
        }}
        onCancel={()=>setCopyConfirm(null)}
      />

      {/* Note editor BottomSheet */}
      <BottomSheet open={!!noteTarget} onClose={()=>setNoteTarget(null)} title={`📝 Note — ${noteTarget ? MONTH_NAMES[parseInt(noteTarget.split("-")[1])-1]+" "+noteTarget.split("-")[0] : ""}`}
        footer={(!can || can("targets","edit")) ? <>
          <Btn onClick={()=>setNoteTarget(null)} variant="outline" color={T.textSub} style={{flex:1}}>Cancel</Btn>
          <Btn onClick={()=>{ monthOps.updateTargetNote(noteTarget, noteText); setNoteTarget(null); }} color={T.accent} style={{flex:2}}>Save Note</Btn>
        </> : <Btn onClick={()=>setNoteTarget(null)} variant="outline" color={T.textSub} style={{flex:1}}>Close</Btn>}>
        <div style={{ padding:"8px 0" }}>
          <textarea value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="e.g. Black Friday push, New ASIN live, Supplier delayed…"
            readOnly={can && !can("targets","edit")}
            rows={3} style={{ width:"100%", background:T.surface, border:`1.5px solid ${T.border}`, borderRadius:RADIUS.md, color:T.text, padding:"12px 14px", fontSize:14, fontFamily:FONT, outline:"none", resize:"vertical", boxSizing:"border-box" }}/>
          {noteText && (!can || can("targets","edit")) && (
            <button onClick={()=>{ setNoteText(""); monthOps.updateTargetNote(noteTarget, ""); }}
              style={{ marginTop:8, background:"transparent", border:"none", color:T.red, fontSize:12, fontFamily:FONT, cursor:"pointer" }}>
              Clear note
            </button>
          )}
        </div>
      </BottomSheet>

      {/* Year sections — 2 columns on desktop, 1 on mobile */}
      <div style={{ display: isMobile ? "block" : "grid", gridTemplateColumns: "1fr", gap: 16, alignItems:"start" }}>
      {visibleYears.slice().sort().filter(y=>y!==currentYear).map(year=>{
        const yearTotal = Array.from({length:12},(_,i)=>{ const k=mkKey(year,i+1); const raw=months[k]?.target||0; if(isSingleMkt&&selectedMkt){ const ov=months[k]?.[`mktTarget_${selectedMkt.id}`]; return ov!==null&&ov!==undefined?ov:Math.round(raw*(selectedMkt.pctOfTotal||0)); } if(isPoolSelection) return Math.round(raw*poolPct); return raw; }).reduce((a,b)=>a+b,0);
        const isExp = expandedYears[year]!==false;
        const isPrev = year<currentYear;
        const isCurrent = year===currentYear;
        const yearColor = isPrev?T.orange:isCurrent?T.text:T.purple;

        // Sparkline data
        const sparkData = Array.from({length:12},(_,i)=>({ t:months[mkKey(year,i+1)]?.target||0, a:months[mkKey(year,i+1)]?.actualSold||0 }));
        const sparkMax = Math.max(1,...sparkData.map(d=>Math.max(d.t,d.a)));

        // Year summary
        const monthTargets = Array.from({length:12},(_,i)=>months[mkKey(year,i+1)]?.target||0);
        const monthActuals = Array.from({length:12},(_,i)=>Math.round((months[mkKey(year,i+1)]?.actualSold||0)*_selPct));
        const totalActual = monthActuals.reduce((a,b)=>a+b,0);
        const avgTarget = Math.round(yearTotal/12);
        const bestMonth = monthTargets.indexOf(Math.max(...monthTargets));
        const worstMonth = monthTargets.indexOf(Math.min(...monthTargets.filter(t=>t>0)));
        // YoY growth
        const prevYearTotal = Array.from({length:12},(_,i)=>months[mkKey(year-1,i+1)]?.target||0).reduce((a,b)=>a+b,0);
        const yoyPct = prevYearTotal > 0 ? Math.round((yearTotal - prevYearTotal)/prevYearTotal*100) : null;

        // Decisions for this year
        const yearDecisions = (decisions||[]).filter(d => {
          try { const dt = d.date instanceof Date ? d.date : new Date(d.date); return dt.getFullYear()===year; } catch { return false; }
        });

        // Previous year for copy
        const sortedYears = visibleYears.slice().sort();
        const yearIdx = sortedYears.indexOf(year);
        const prevYear = yearIdx > 0 ? sortedYears[yearIdx-1] : null;

        return (
          <div key={year} data-tour={isCurrent?"year-current":year>currentYear?"year-next":undefined} style={{ marginBottom: isMobile ? 20 : 0 }}>
            {/* Year header */}
            <div onClick={()=>setExpandedYears(p=>({...p,[year]:!isExp}))} style={{
              display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
              width:"100%", background:"transparent", border:`1px solid ${T.border}`,
              borderRadius:isExp?`${RADIUS.md}px ${RADIUS.md}px 0 0`:RADIUS.md,
              padding:"10px 14px", fontFamily:FONT, cursor:"pointer",
              borderBottom: isExp ? `1px solid ${T.border}` : `1px solid ${T.border}`,
            }}>
              <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                <span style={{ fontSize:17, fontWeight:700, color:yearColor, letterSpacing:-0.3 }}>{year}</span>
                {isPrev&&<span style={{ fontSize:10,color:T.orange,background:T.orange+"18",padding:"2px 8px",borderRadius:RADIUS.pill,fontWeight:600 }}>Previous</span>}
                {isCurrent&&<span style={{ fontSize:10,color:T.accent,background:T.accent+"18",padding:"2px 8px",borderRadius:RADIUS.pill,fontWeight:600 }}>Current</span>}
                {!isMobile && <>
                  <span style={{ width:1, height:12, background:T.border, flexShrink:0 }}/>
                  <span style={{ fontSize:10, color:T.textSub }}><strong style={{ color:T.text }}>{yearTotal.toLocaleString()}</strong> target</span>
                  {totalActual>0&&<span style={{ fontSize:10, color:T.textSub }}><strong style={{ color:T.green }}>{totalActual.toLocaleString()}</strong> sold</span>}
                  <span style={{ fontSize:10, color:T.textSub }}><strong style={{ color:T.text }}>{avgTarget}</strong> avg/mo</span>
                  {monthTargets.some(t=>t>0)&&<span style={{ fontSize:10, color:T.textSub }}>Best: <strong style={{ color:T.green }}>{MONTH_NAMES[bestMonth].slice(0,3)} ({monthTargets[bestMonth]})</strong></span>}
                  {yoyPct!==null&&<span style={{ fontSize:10, color:T.textSub }}>YoY: <strong style={{ color:yoyPct>=0?T.green:T.red }}>{yoyPct>0?"+":""}{yoyPct}%</strong></span>}
                  {totalActual>0&&yearTotal>0&&<span style={{ fontSize:10, color:T.textSub }}>Hit: <strong style={{ color:totalActual>=yearTotal?T.green:T.orange }}>{Math.round(totalActual/yearTotal*100)}%</strong></span>}
                </>}
              </div>

              {/* Sparkline — desktop only */}
              {!isMobile && (
                <svg width={100} height={28} viewBox="0 0 100 28" style={{ flexShrink:0 }}>
                  {/* Target line — dotted */}
                  <polyline fill="none" stroke={T.accent} strokeWidth={1.2} strokeDasharray="3 2"
                    points={sparkData.map((d,i)=>`${i*100/11},${28 - d.t/sparkMax*24}`).join(" ")}/>
                  {/* Actual line — solid */}
                  {totalActual > 0 && <polyline fill="none" stroke={T.green} strokeWidth={1.5}
                    points={sparkData.filter(d=>d.a>0).map((d,i)=>`${sparkData.indexOf(d)*100/11},${28 - d.a/sparkMax*24}`).join(" ")}/>}
                </svg>
              )}

              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <span style={{ fontSize:13,color:isSingleMkt?selectedMktColor:T.green,fontWeight:700, whiteSpace:"nowrap" }}>{yearTotal.toLocaleString()} units</span>
                {/* Copy button — desktop only */}
                {!isMobile && prevYear && (!can || can("targets","edit")) && (<>
                  <Tooltip content={`Copy all 12 monthly targets from ${prevYear} into ${year}${copyGrowth?` with ${copyGrowth>0?"+":""}${copyGrowth}% growth`:""}. Example: Jan ${prevYear} was 500 units${copyGrowth?` + ${copyGrowth}% = ${Math.round(500*(1+copyGrowth/100))}`:""} for Jan ${year}. Overwrites existing ${year} targets.`}>
                  <button onClick={e=>{e.stopPropagation(); setCopyConfirm({fromYear:prevYear, toYear:year, growthPct:copyGrowth});}}
                    style={{ fontSize:12, background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, padding:"3px 8px", cursor:"pointer", fontFamily:FONT, color:T.textSub, whiteSpace:"nowrap" }}>
                    📋 Copy {prevYear}
                  </button>
                  </Tooltip>
                  <Tooltip content="Growth % applied when copying. E.g. 20 = each month gets +20%. Set to 0 for exact copy.">
                  <div onClick={e=>e.stopPropagation()} style={{ display:"flex", alignItems:"center", gap:2 }}>
                    <input type="number" value={copyGrowth??""}  placeholder="0" onClick={e=>e.stopPropagation()} onChange={e=>setCopyGrowth(e.target.value===""?null:parseInt(e.target.value)||0)}
                      style={{ width:36, background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.text, padding:"2px 4px", fontSize:12, fontFamily:FONT, textAlign:"center", outline:"none" }}/>
                    <span style={{ fontSize:12, color:T.textSub }}>%</span>
                  </div>
                  </Tooltip>
                  <Tooltip content={"Auto-fill " + year + " targets from " + prevYear + " actual sold data + growth %. Uses real performance instead of planned targets."}>
                  <button onClick={e=>{e.stopPropagation();
                    const mult = 1 + (copyGrowth||0)/100;
                    for (let m=1; m<=12; m++) {
                      const srcKey = mkKey(prevYear, m);
                      const destKey = mkKey(year, m);
                      const actual = months[srcKey]?.actualSold || months[srcKey]?.target || 0;
                      if (actual > 0) monthOps.updateTarget(destKey, String(Math.round(actual * mult)));
                    }
                  }}
                    style={{ fontSize:10, background:T.green+"15", border:`1px solid ${T.green}30`, borderRadius:RADIUS.sm, padding:"3px 8px", cursor:"pointer", fontFamily:FONT, color:T.green, whiteSpace:"nowrap", fontWeight:600 }}>
                    💡 From actuals
                  </button>
                  </Tooltip>
                </>)}
                <span style={{ color:T.textSub, fontSize:12, transition:`transform 0.22s ${SPRING}`, display:"inline-block", transform:isExp?"rotate(180deg)":"rotate(0)", padding:"2px 4px" }}>▼</span>
              </div>
            </div>

            {/* Expandable content */}
            <div style={{
              maxHeight:isExp?"3000px":"0",
              overflow:"hidden",
              transition:`max-height 0.38s ${SPRING}`,
            }}>
              <div style={{ background:"transparent", border:"none", padding:12 }}>

                {/* Copy growth % input — shown when copy is about to happen */}
                {!isMobile && prevYear && (!can || can("targets","edit")) && (
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10, fontSize:11, color:T.textSub }}>
                    <Tooltip content="Year-over-year growth percentage. Set to 20 for +20% growth, -10 for 10% decline. Applied to every month when copying. Example: if Jan was 500 and growth is 30%, Jan becomes 650."><span >Growth %:</span></Tooltip>
                    <input type="number" value={copyGrowth??""}  placeholder="0" onChange={e=>setCopyGrowth(e.target.value===""?null:parseInt(e.target.value)||0)}
                      style={{ width:50, background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm, color:T.text, padding:"3px 6px", fontSize:12, fontFamily:FONT, textAlign:"center", outline:"none" }}/>
                    <span style={{ color:T.textMuted }}>applied when copying from {prevYear}</span>
                  </div>
                )}

                {/* Month grid */}
                <div data-tour="target-grid" style={{ display:"grid", gridTemplateColumns: isMobile ? "repeat(auto-fill,minmax(80px,1fr))" : "repeat(6,1fr)", gap:8 }}>
                  {(()=>{
                    let cb=0, csb=0;
                    return Array.from({length:12},(_,i)=>{
                      const k=mkKey(year,i+1); const t=months[k]?.target||0;
                      const pct=simOpen?(simMode==="all"?simPct:(simCustom[k]||0)):0;
                      const rev=t*(weightedPayout||0); const simRev=Math.round(t*(1+pct/100))*(weightedPayout||0);
                      cb+=rev; csb+=simRev;
                      return <MonthCard key={k} year={year} monthIdx={i}
                        months={months} marketplaces={marketplaces} orders={orders}
                        monthOps={monthOps} saveStatus={saveStatus}
                        selMkts={selMkts} showDanger={showDanger} dangerPct={dangerPct}
                        isPrev={isPrev} currentYear={currentYear}
                        lastEditedKey={lastEditedKey} setLastEditedKey={setLastEditedKey}
                        animKey={animKey} animDelay={animDelays[`${year}_${i}`]||0} can={can}
                        modifiers={modifiers} initialStock={effectiveStartingStock}
                        weightedPayout={weightedPayout} costPerUnit={initialCostPerUnit||0} shrinkagePct={shrinkagePct} simPct={simOpen?(simMode==="all"?simPct:(simCustom[k]||0)):0}
                        monthBalance={cb} simMonthBalance={csb}
                        isPoolSelection={isPoolSelection} poolPct={poolPct} selectedPoolName={selectedPool?.name||""}
                        onEditNote={(k2,txt)=>{ setNoteTarget(k2); setNoteText(txt||""); }}/>;
                    });
                  })()}
                </div>

                {/* Decision badges with impact — desktop only */}
                {!isMobile && yearDecisions.length > 0 && (
                  <div style={{ marginTop:10, display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
                    {yearDecisions.map(d => {
                      let mIdx; try { const dt = d.date instanceof Date ? d.date : new Date(d.date); mIdx = dt.getMonth(); } catch { mIdx = 0; }
                      // Compute impact: linked modifiers + expenses
                      const linkedMods = (modifiers||[]).filter(m => m.decisionId === d.id);
                      const linkedExp = (recurringExpenses||[]).filter(r => r.decisionId === d.id);
                      let unitsPct = 0, incomePct = 0, monthlyExp = 0;
                      for (const mod of linkedMods) {
                        if (mod.type === "units") unitsPct += (mod.value || 0) * 100;
                        if (mod.type === "payout") incomePct += (mod.value || 0) * 100;
                      }
                      monthlyExp = linkedExp.reduce((s, r) => s + (r.amount || 0), 0);
                      const hasImpact = unitsPct !== 0 || incomePct !== 0 || monthlyExp > 0;
                      const isOn = decImpactOn[d.id] !== false; // default ON
                      const col = d.color || T.accent;
                      return (
                        <Tooltip key={d.id} content={hasImpact ? `${d.name}: ${unitsPct!==0?`Units ${unitsPct>0?"+":""}${Math.round(unitsPct)}%. `:""}${incomePct!==0?`Income ${incomePct>0?"+":""}${Math.round(incomePct)}%. `:""}${monthlyExp>0?`${currSym()}${monthlyExp}/mo expense. `:""}${isOn?"Active — click to disable":"Disabled — click to enable"}` : d.name}>
                        <button onClick={()=>{ if(hasImpact) setDecImpactOn(prev=>({...prev,[d.id]:!isOn})); }}
                          style={{ fontSize:10, padding:"3px 8px", borderRadius:RADIUS.pill, background:isOn?col+"18":T.surface, border:`1px solid ${isOn?col+"33":T.border}`, color:isOn?col:T.textSub, fontWeight:600, cursor:hasImpact?"pointer":"default", fontFamily:FONT, opacity:isOn?1:0.5, display:"inline-flex", alignItems:"center", gap:4 }}>
                          {d.icon||"📌"} {MONTH_NAMES[mIdx].slice(0,3)}: {d.name}
                          {hasImpact && (
                            <span style={{ fontSize:12, fontWeight:700, color:isOn?col:T.textSub }}>
                              {unitsPct!==0?`${unitsPct>0?"+":""}${Math.round(unitsPct)}%`:""}
                              {monthlyExp>0?` -${currSym()}${monthlyExp}`:""}
                              {isOn?"●":"○"}
                            </span>
                          )}
                        </button>
                        </Tooltip>
                      );
                    })}
                  </div>
                )}

                {/* Year summary stats moved to header row */}
              </div>
            </div>
          </div>
        );
      })}
      </div>{/* end year grid */}
    </div>
  );
}
